import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest, HttpResponse} from '@angular/common/http';
import {Action, select, Store} from '@ngrx/store';
import {
    BehaviorSubject,
    combineLatest,
    forkJoin,
    forkJoin as observableForkJoin,
    from,
    Observable,
    of as observableOf,
    Subject
} from 'rxjs';
import {
    catchError,
    concatMap,
    delay,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    switchMap,
    take,
    takeUntil,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarRef as MatSnackBarRef} from '@angular/material/legacy-snack-bar';
import {Router} from '@angular/router';
import {FileItem, FileUploader, FileUploaderOptions, ParsedResponseHeaders} from 'ng2-file-upload';

import {PreferenceItem, Preferences} from '@app/core/model/user-preferences.model';
import * as ApplicationSettingsActions from './application-settings.actions';
import {
    AssignApplicationExpirationDate,
    AssignIntegrationProcessConfigModel,
    GetMasterFields,
    UpdateDataSourceConfig
} from './application-settings.actions';
import {environment} from '@env/environment';
import {APP_LAST_STEP, Application} from '../models/application.model';
import {DataSourcePathMap, DataSourceType, DataSourceTypeTitleMap} from '../models/data-source-type.model';
import {DataSourceConfig} from '../models/data-source-config.model';
import {Integration, PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN} from '../models/integration.model';
import {
    FILTRATIONS,
    IntegrationTask,
    SSO_FILTRATIONS,
    STREAM,
    STREAM_TRANSFORMATIONS,
    TRANSFORMATIONS
} from '../models/integration-task.model';
import {UserPreferencesService} from '@app/core/service/user-preferences.service';
import * as fromApplicationSettings from './application-settings.reducers';
import {getAllowedApplications, getApplicationSettings} from './application-settings.reducers';
import {IntegrationMode} from '../models/integration-mode.model';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {IntegrationState} from '../models/integration-state.model';
import {FTPConfig} from '../models/ftp-config';
import {NEW_ORG_IDENTIFIER, NEW_QWIKLABS_SCHOOL_IDENTIFIER, OrganizationsMapping} from '../models/organizationsMapping.model';
import {BLACKBOARD_NODES, BLACKBOARD_ROOT, OrgMapping} from '../models/orgMapping.model';
import {TokenService} from '@app/core/security/token.service';
import * as authAction from '../../../common/auth.actions';
import {AuthActionTypes, LoadVendorApps, SignOutClearDataAction, UpdateUserApps} from '../../../common/auth.actions';
import {User} from '@app/core/model/user';
import {RankOneDistrictItem} from '../models/sftp-config.model';
import {
    ApplicationSettingsService,
    DATA_SOURCE_CONFIG_GOOGLE_OBJECT_ID,
    LOGIN_CREDENTIALS_ATTRIBUTES_MAP
} from '../application-settings.service';
import {CronConfig, ProcessConfig} from '../models/process-config.model';
import {IntegrationTaskMap} from '../models/integration-task-map.model';
import {Spinner} from '@app/core/service/spinner.service';
import {NgcSearchService} from '../../../search/search.service';
import * as IntegrationActions from '@app/core/reducers/integration/integration.actions';
import {
    GetDefaultSchemaAction,
    GetProviderApplicationsAction,
    IntegrationGettingDefaultAction,
    UpdateVendorCurrentAppAction
} from '@app/core/reducers/integration/integration.actions';
import {IntegrationService} from '@app/core/service/integration.service';
import {DomainOfUniqueness} from '../models/domain-of-uniqueness.model';
import {AttributeMetadataModel, copyData, genId, IntegrationRulesMap, RequestedFilterViewModel} from '@app/core/model/integration-models';
import {PrivacyPolicyService, SavePolicyMethod} from '../../privacy-policy/privacy-policy.service';
import {ApplicationType} from '../models/application-type.model';
import {OrgItem} from '../models/orgItem.model';
import {LogService} from '@app/core/log/log.service';
import {MESSAGES} from '@app/core/model/messages';
import {
    getAuthenticatedUserApplications,
    getDefaultSchema,
    getDeletingApplications,
    getProviderApplications,
    getSchoolYearRolloverSettings,
    getUserOrganization,
    isAuthenticated
} from '@app/app.reducers';
import {LTIConfig} from '@app/views/admin/application-settings/models/lti-config.model';
import {OrgsService} from '@app/core/service/orgs.service';
import {MappingRule, SCHOOL_ID} from '@app/views/admin/application-settings/models/mapping-rules.model';
import {DataSourceFriendlyName} from '@app/views/admin/application-settings/models/data-source-type.model';
import {MetadataModel} from '@app/core/model/filtering/metadata-model';
import {EntityFilterModel} from '@app/core/model/filtering/entity-filter-model';
import {FilterDefaultPredicateMap} from '@app/core/model/filtering/filter-predicate';
import {
    DictionaryModel,
    GRADE_DICTIONARY_NAME,
    QWIKLABS_ACTIVATED_COURSES_DICTIONARY_NAME,
    QWIKLABS_COURSE_MAPPING_DICTIONARY_NAME,
    SSO_APPLICATIONS_DICTIONARY_NAME
} from '@app/views/admin/application-settings/models/dictionary.model';
import {DictionaryEntryModel} from '@app/views/admin/application-settings/models/dictionary-entry-model';
import {Argument} from '@app/core/model/filtering/argument';
import {FiltersListService} from '@app/views/admin/application-settings/components/filters/filters-list-wrapper/filters-list.service';
import {RosterAccessProgress} from '@app/views/admin/application-settings/models/roster-access-progress.model';
import {QwiklabsCourse, QwiklabsDictionaryEntryModel} from '@app/views/admin/application-settings/models/qwiklabs-course.model';
import {SnackBarService} from '@app/core/service/snack-bar.service';
import {GoogleClassroomConfig} from '@app/views/admin/application-settings/models/google-classroom';
import {ApplicationProviderListModel} from '@app/views/admin/application-settings/models/application-provider-list.model';
import {AnnouncementService, Claim, PolicyConfig} from '@app/views/common/dialog-announcement/announcement.service';
import {
    MasterFieldsMergingMetadataModel,
    MasterFieldsMergingRule,
    MasterFieldsModel
} from '@app/views/admin/application-settings/models/master-fields.model';
import {AuthenticationService} from '@app/core/service/authentication.service';
import {FTPUser} from '@app/views/admin/application-settings/models/ftp-user.model';
import {ISchoolYearRolloverSettings} from '@app/core/model/rollover-model';
import {DataAttribute} from '@app/views/admin/application-settings/models/data-attribute.model';
import {isLoginRoute} from '@app/core/main.module.routes';
import {ApplicationGalleryModel} from '@app/views/admin/application-settings/models/application-gallery.model';
import {FEATURE_TYPES} from '@app/views/admin/application-settings/models/application-properties.model';
import {CustomerService} from '@app/core/service/customer.service';
import {ConfirmDialogComponent} from '@app/core/comp/dialog/confirm-dialog/confirm-dialog.component';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import * as auth from '@app/views/common/auth.actions';
import {TestConnectionSnackbarComponent} from '@app/views/admin/application-settings/components/delivery-config/test-connection/test-connection-snackbar/test-connection-snackbar.component';
import {SharedPoolResponseData} from '@app/core/model/shared-pool.model';
import {
    ENABLE_SUPPORT_INCREMENTAL_SYNC,
    FEATURE_TOGGLE_LEVEL,
    FeatureToggleModel,
    SHOW_DISTRICT_FEATURE_TOGGLE_ON_DISTRICT_APPLICATION_LEVEL
} from '@app/views/common/feature-toggles/featue-toggle.model';
import {FeatureTogglesService} from '@app/core/service/feature-toggles.service';
import {FormControl} from '@angular/forms';
import {LDAPConfig} from '@app/views/admin/application-settings/models/ldap-config.model';
import {Webhook} from '@app/views/admin/application-settings/models/provisioning.model';
import {PIIShieldService} from '@app/core/service/pii-shield.service';
import {PII_LEVEL} from '@app/views/admin/application-settings/components/basic/pii-shield-level-info/pii-shield.model';

@Injectable()
export class ApplicationSettingsEffects {
    spinner: Spinner;
    snackBar: MatSnackBar;

    readonly GET_INTEGRATION_RULES_ERROR_MESSAGE = 'Unknown error occurred while loading Filter Rules data.';
    readonly GET_INTEGRATION_ORGS_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while loading Schools Mapping data.';
    readonly SAVE_DELIVERY_CONFIG_ERROR_MESSAGE = 'Unknown error occurred while saving Delivery Config data.';
    readonly GENERATE_LTI_CREDENTIALS_ERROR_MESSAGE = 'Unknown error occurred while generating credentials.';
    readonly SAVE_INTEGRATION_ORGS_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while saving Schools Mapping data.';
    readonly SAVE_MAPPING_RULES_ERROR_MESSAGE = 'Unknown error occurred while saving Mapping rules data.';
    readonly SAVE_MAPPING_RULES_ARCHIVE_OU_ERROR_MESSAGE = 'Unknown error occurred while saving Mapping rules Archive OU data.';
    readonly SAVE_INTEGRATION_ENTITY_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while saving Entity Mapping data.';
    readonly SAVE_INTEGRATION_DATA_REQUIREMENTS_ERROR_MESSAGE = 'Unknown error occurred while saving Data Requirements.';
    readonly SAVE_INTEGRATION_ATTRIBUTES_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while saving Attributes Mapping data.';
    readonly SAVE_GRADE_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while saving Grade Mapping data.';
    readonly SAVE_STAFF_ACCESS_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while saving Staff Access Mapping data.';
    readonly RESET_GRADE_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while resetting Grade Mapping data.';
    readonly MAIN_SAVE_ERROR_MESSAGE = 'Unknown error occurred while saving.';
    readonly SAVE_EDIT_INTEGRATION_ERROR_MESSAGE = 'Sorry, you can`t edit Data Schema that is in usage.';
    readonly DELETE_INTEGRATION_ORG_MAPPING_ERROR_MESSAGE = 'Unknown error occurred while deleting School Mapping.';
    readonly PUBLISH_SUCCESSFUL_MESSAGE = 'Your Application Published Successfully.';
    readonly PUBLISH_CHANGES_SUCCESSFUL_MESSAGE = 'Changes to Your Application Published Successfully.';
    readonly UNPUBLISH_SUCCESSFUL_MESSAGE = 'Your Application Unpublished Successfully.';
    readonly GET_APPLICATION_DATA_ERROR_MESSAGE = 'Unknown error occurred while loading Application data.';
    readonly SAVE_MERGING_RULES_ERROR_MESSAGE = 'Unknown error occurred while saving Domains of Uniqueness.';
    readonly GET_MASTER_FIELDS_ERROR_MESSAGE = 'Unknown error occurred while loading Master Fields data.';
    readonly SAVE_MASTER_FIELDS_ERROR_MESSAGE = 'Unknown error occurred while saving Master Fields data.';
    readonly MASTER_FIELD_ALREADY_EXIST_ERROR_MESSAGE = 'Created Master Field already exists.';
    readonly SUCCESS_REVOKE_ACCESS = 'Access was successfully revoked.';
    readonly UNSUCCESS_REVOKE_ACCESS = 'Revoke access failed.';
    readonly LOGIN_CREDENTIALS_ATTRIBUTES_MAP = LOGIN_CREDENTIALS_ATTRIBUTES_MAP;

    getApplication = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_APPLICATION),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]) => this.applicationSettingsService.getApplication(state.application.guid)
                .pipe(
                    mergeMap(
                        (application: Application) => {
                            const actions = [
                                <Action>{
                                    type: ApplicationSettingsActions.ASSIGN_APPLICATION,
                                    payload: application
                                }
                            ];

                            if (application.modes
                                && (application.modes.length === 1)
                                && !application.modes.includes(<string>state.integrationMode)) {
                                actions.push(<Action>{
                                    type: ApplicationSettingsActions.SET_INTEGRATION_MODE,
                                    payload: <IntegrationMode>application.modes[0]
                                });
                            }

                            return actions;
                        }
                    ),
                    catchError(error => {
                        let message = 'Can`t get an application.';

                        const user: User = this.authenticatedUserService.authenticatedUser;
                        this.searchService.restoreRecent(user);

                        if (this.searchService._recentApplications) {
                            const cnt = this.searchService._recentApplications.length;
                            const newListApps = this.searchService._recentApplications.filter(
                                ar => ar.guid != state.application.guid
                            );
                            if (cnt > newListApps.length) {
                                message = 'The application no longer exists';
                                this.searchService._recentApplications = newListApps;
                                this.searchService.recentApplicationsChanges.next(newListApps);
                            }
                        }

                        return from([
                            {
                                type: 'FAIL',
                                payload: {error: error}
                            },
                            {
                                type: ApplicationSettingsActions.SET_ERROR,
                                payload: {message: message}
                            }
                        ]);
                    })
                ))
        )
    );

    savePrivacyPolicyDocument = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_PRIVACY_POLICY_DOCUMENT),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([action, state]: [ApplicationSettingsActions.SavePrivacyPolicyDocument, fromApplicationSettings.State]) => {

                const newDocumentTitle = `Privacy Policy for ${state.application.name}`;
                if (action.payload.savePolicyMethod === SavePolicyMethod.FILE_FROM_EDITOR &&
                    (!action.payload.value['name'] || action.payload.value['title'] !== newDocumentTitle)
                ) {
                    action.payload.value['name'] = `vendor.policy.${state.application.guid}`;
                    action.payload.value['title'] = newDocumentTitle;
                }

                return this.privacyPolicyService.savePrivacyPolicy(action.payload, 'vendorPolicyAdmin', state.application.guid)
                    .pipe(
                        mergeMap(() => [{
                            type: ApplicationSettingsActions.SET_IS_LOADING,
                            payload: false
                        }]),
                        catchError((error: ErrorEvent) => {
                            const message = error.message;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    acceptPrivacyPolicy = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.ACCEPT_PRIVACY_POLICY_DOCUMENT),
            switchMap((action: ApplicationSettingsActions.AcceptPrivacyPolicyDocument) =>
                this.store.pipe(select(getApplicationSettings)).pipe(
                    filter((state: fromApplicationSettings.State) => state.application.guid && !!state.actualPrivacyPolicyDocument),
                    take(1),
                    map(state => [action, state])
                )
            ),
            mergeMap(([action, state]: [ApplicationSettingsActions.AcceptPrivacyPolicyDocument, fromApplicationSettings.State]) => this.announcementService.markAsAccepted(
                state.actualPrivacyPolicyDocument.name,
                state.actualPrivacyPolicyDocument.version,
                state.actualPrivacyPolicyDocument.rule.id,
                { application: this.privacyPolicyService.getAppGuidFromClaimName(state.actualPrivacyPolicyDocument.name)
                        || state.actualPrivacyPolicyDocument['defaultApplicationGuid']},
                state.application.guid
            ).pipe(
                mergeMap((policyConfig: PolicyConfig) => [{
                    type: ApplicationSettingsActions.ASSIGN_ACTUAL_PRIVACY_POLICY_DOCUMENT,
                    payload: policyConfig.claims[0]
                }
                ]),
                catchError((error: ErrorEvent) => {
                    const message = error.message;
                    this.snackBarService.showSnackBarMessage(message, 3000);
                    return observableOf({type: 'FAIL', payload: {error: error}});
                })
            ))
        )
    );

    getActualPrivacyPolicyDocument = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_ACTUAL_PRIVACY_POLICY_DOCUMENT),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            filter(() => this.authenticatedUserService.isProvider()),
            mergeMap(([action, state]: [ApplicationSettingsActions.GetActualPrivacyPolicyDocument, fromApplicationSettings.State]) => {
                const {defaultApplication, integrationMode} = action.payload;

                if (state.application && state.application.guid && state.application.modes || state.integrationMode && defaultApplication) {
                    this.store.dispatch(new ApplicationSettingsActions.SetIsLoading(true));

                    const providerApplicationGuid = state?.application?.guid || null;
                    const mode = integrationMode || (state?.application?.modes ? state.application.modes[0] : state.integrationMode);
                    const defaultApp = state.allowedApplications.find((application: Application) =>
                        ((application && state.application && application.icon == state.application.icon) ||
                            (defaultApplication && application.icon == defaultApplication?.icon))
                        && application.modes && (application.modes.indexOf(mode) > -1)
                    );
                    const claimName = `provider.application.${mode}.${defaultApp?.guid}`;

                    return this.announcementService.checkClaimsForProviderApplication(claimName, providerApplicationGuid).pipe(
                        map((policyConfig: PolicyConfig) => {
                            const actualDocument = policyConfig.claims[0];
                            if (actualDocument) {
                                actualDocument.defaultApplicationGuid = state.application.defaultApplicationGuid;

                                if (actualDocument.userConfirmations) {
                                    actualDocument.userConfirmations = actualDocument.userConfirmations.filter(value => value['application'] === providerApplicationGuid);
                                }
                            }

                            return actualDocument;
                        }),
                        mergeMap((claim: Claim) => {
                            if (this.spinner && this.spinner.active) {
                                this.spinner.stop();
                            }

                            return [{
                                type: ApplicationSettingsActions.ASSIGN_ACTUAL_PRIVACY_POLICY_DOCUMENT,
                                payload: claim
                            },
                            {
                                type: ApplicationSettingsActions.SET_IS_LOADING,
                                payload: false
                            }];
                        }),
                        catchError((error: ErrorEvent) => {
                            const message = error.message;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
                } else {
                    if (this.spinner && this.spinner.active) {
                        this.spinner.stop();
                    }

                    return [{
                        type: ApplicationSettingsActions.SET_IS_LOADING,
                        payload: false
                    }];
                }
            })
        )
    );

    createOrUpdateApplication = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CREATE_OR_UPDATE_APPLICATION),
            map((action: ApplicationSettingsActions.CreateOrUpdateApplication) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.authenticatedUserService.authenticatedUser$,
                this.userPreferencesService.preferences$
            ),
            concatMap(([payload, state, user, preferences]:
                           [{ application: Application; iconUploader: FileUploader; stopSpinner: boolean; privacyPolicy: any; publish: boolean },
                               fromApplicationSettings.State, User, Preferences]
            ) => {

                const uploader = payload.iconUploader;
                const stopSpinner = payload.stopSpinner;
                const publish = payload.publish;
                const privacyPolicy = payload.privacyPolicy;
                const body = <Application>Object.assign({}, state.application, payload.application);
                body.ownerId = state.application.ownerId || body.ownerId || user.orgGuid;
                body.vendorName = body.vendorName || user.orgName;

                if (!state.application.dataSourceRef && this.authenticatedUserService.isVendor(user)) {
                    const integrationMode = ApplicationSettingsService.getIntegrationMode(this.authenticatedUserService.isVendor(user));
                    body.modes = [integrationMode];
                } else {
                    if (state.integrationMode) {
                        body.modes = [state.integrationMode];
                    }
                }

                if (uploader) {
                    const uploaderOptions: FileUploaderOptions = {};
                    uploaderOptions.headers = [
                        { name: 'X-Auth-Token', value: this.tokenService.getToken() }
                    ];
                    uploaderOptions.itemAlias = 'app_icon'; // set this parameter
                    uploaderOptions.url = `${environment.datahubBaseURI}/api/v2/applications`;
                    uploaderOptions.method = 'post';
                    uploaderOptions.additionalParameter = body;
                    uploader.setOptions(uploaderOptions);

                    uploader.onBuildItemForm = (keyFile: any, formData: FormData) => {
                    // formData.append('newApplication', new Blob([JSON.stringify(body)], {type: 'application/json'}));
                    };

                    uploader.uploadAll();

                    uploader.onSuccessItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders): any => {

                        const application = <Application>JSON.parse(response);
                        const actions = [
                            <Action>{
                                type: ApplicationSettingsActions.ASSIGN_APPLICATION,
                                payload: application
                            }
                        ];

                        if (privacyPolicy) {
                            actions.push(
                                <Action>{
                                    type: ApplicationSettingsActions.SAVE_PRIVACY_POLICY_DOCUMENT,
                                    payload: privacyPolicy
                                }
                            );
                        }

                        if (stopSpinner) {
                            actions.push(
                                <Action>{
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }
                            );
                        }

                        if (publish) {
                            actions.push(
                                <Action>{
                                    type: ApplicationSettingsActions.PUBLISH
                                }
                            );
                        }

                        if (this.authenticatedUserService.isProvider(user) && application.configurationStep === 0) {
                            actions.push(new GetProviderApplicationsAction());
                        }

                        // update User Preferences
                        const currentApp = preferences.getPreferencesItem('currentApp');
                        const updateUserPreferences  = !(currentApp && ((currentApp.selectedId === application.guid) && (currentApp.selectedFriendlyName === application.name)));
                        if (this.authenticatedUserService.isVendor(user)) {

                            actions.push(new LoadVendorApps({user: user}));

                            if (updateUserPreferences) {
                                const userPreferenceItem = new PreferenceItem(application.name, application.guid);
                                actions.push(new authAction.AuthenticatedUpdateExtraUserPreferenceItemAction(
                                    {key: 'currentApp', userPreferenceItem: userPreferenceItem}
                                ));
                            }
                        }

                        if (!application.modes.includes(state.integrationMode)) {
                            actions.push(<Action>{type: ApplicationSettingsActions.INIT_INTEGRATION_STATE});
                        }

                        return actions;
                    };
                    uploader.onErrorItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders): any => {
                        if (status === 401) {
                            this.store.dispatch(new SignOutClearDataAction());

                            let returnUrl = (this.router.routerState.snapshot.root.queryParams['returnUrl']
                                ? this.router.routerState.snapshot.root.queryParams['returnUrl']
                                : this.router.routerState.snapshot.url);
                            returnUrl = (isLoginRoute(returnUrl) ? '' : returnUrl);
                            this.authenticationService.redirectToLoginPage({queryParams: {returnUrl: returnUrl}});
                            return observableOf({type: 'FAIL', payload: {error: JSON.parse(response)} });
                        } else {
                            return observableOf({type: 'FAIL', payload: {error: JSON.parse(response)} });
                        }
                    };

                } else {

                    return this.applicationSettingsService.createOrUpdateApplication(body)
                        .pipe(
                            mergeMap((application: Application) => {
                                const actions = [
                                    <Action>{
                                        type: ApplicationSettingsActions.ASSIGN_APPLICATION,
                                        payload: application
                                    }
                                ];

                                if (privacyPolicy) {
                                    actions.push(
                                        <Action>{
                                            type: ApplicationSettingsActions.SAVE_PRIVACY_POLICY_DOCUMENT,
                                            payload: privacyPolicy
                                        }
                                    );
                                }

                                if (stopSpinner) {
                                    actions.push(
                                        <Action>{
                                            type: ApplicationSettingsActions.SET_IS_LOADING,
                                            payload: false
                                        }
                                    );
                                }

                                if (publish) {
                                    actions.push(
                                        <Action>{
                                            type: ApplicationSettingsActions.PUBLISH
                                        }
                                    );
                                }

                                if (this.authenticatedUserService.isProvider(user) && application.configurationStep === 0) {
                                    actions.push(new GetProviderApplicationsAction());
                                }

                                // update User Preferences
                                const currentApp = preferences?.getPreferencesItem('currentApp');
                                const updateUserPreferences = !(currentApp
                                    && ((currentApp.selectedId === application.guid)
                                        && (currentApp.selectedFriendlyName === application.name))
                                );
                                if (this.authenticatedUserService.isVendor(user)) {

                                    actions.push(new LoadVendorApps({user: user}));

                                    if (updateUserPreferences) {
                                        const userPreferenceItem = new PreferenceItem(application.name, application.guid);
                                        actions.push(new authAction.AuthenticatedUpdateExtraUserPreferenceItemAction(
                                            {key: 'currentApp', userPreferenceItem: userPreferenceItem}
                                        ));
                                    }
                                }

                                if (application.modes && !application.modes.includes(state.integrationMode)) {
                                    actions.push({type: ApplicationSettingsActions.INIT_INTEGRATION_STATE});
                                }

                                if (this.authenticatedUserService.isSysAdmin(user)) {
                                    this.store.dispatch(new IntegrationActions.UpdateAppInAllVendorApplicationsAction(
                                        application as ApplicationGalleryModel
                                    ));
                                }

                                return actions;
                            }),
                            catchError(error => {
                                const message = 'Can`t create an application.';
                                return observableOf({type: 'SET_ERROR', payload: {message: message}});
                            })
                        );
                }
            })
        )
    );

    getDefaultApplications = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_DEFAULT_APPLICATIONS),
            map((action: ApplicationSettingsActions.GetDefaultApplications) => action.payload),
            filter(() => this.authenticatedUserService.isAuthenticated()),
            concatMap((stopSpinner: boolean) => this.applicationSettingsService.getDefaultApplications()
                .pipe(
                    withLatestFrom(
                        this.authenticatedUserService.authenticatedUser$,
                        this.store.pipe(select(getUserOrganization))
                    ),
                    mergeMap(
                        ([defaultApplications, user, organization]: [Application[], User, DirectoryOrganization]) => {
                            const result = [];
                            if (this.authenticatedUserService.isProvider(user)) {
                                if (organization?.host) {
                                    // Filter and do not allow Google Classroom Export for orgs where host !== 'coursera.gg4l.com' || 'qwiklabs.gg4l.com'
                                    defaultApplications = ApplicationSettingsService.filterApplicationModesForGoogleClassroom(
                                        defaultApplications, organization?.host);
                                } else {
                                    if (this.authenticatedUserService.isProvider(user)) {
                                        result.push(new auth.GetUserOrganizationAction(user?.orgGuid));
                                    }
                                }
                            }

                            result.push(
                                <Action>{
                                    type: ApplicationSettingsActions.SET_DEFAULT_APPLICATIONS,
                                    payload: defaultApplications
                                }
                            );

                            if (this.authenticatedUserService.isVendor(user)) {
                                result.push(
                                    <Action>{
                                        type: ApplicationSettingsActions.SET_VENDOR_ALLOWED_APPLICATIONS,
                                        payload: [...defaultApplications]
                                    }
                                );
                            } else {
                                result.push(
                                    <Action>{
                                        type: ApplicationSettingsActions.SET_PROVIDER_ALLOWED_APPLICATIONS,
                                        payload: [...defaultApplications]
                                    }
                                );
                            }

                            if (stopSpinner) {
                                result.push(<Action>{
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                });

                                if (this.spinner && this.spinner.active) {
                                    this.spinner.stop();
                                }
                            }

                            return result;
                        }
                    ),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    getDataSourceConfig = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_DATA_SOURCE_CONFIG),
            map((action: ApplicationSettingsActions.GetDataSourceConfig) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.store.pipe(select(getAllowedApplications))
            ),
            concatMap(([payload, state, allowedApplications]: [{ dname: string; stopSpinner: boolean; createIntegration: boolean }, fromApplicationSettings.State, Application[]]) => {
                const getDataSourceConfig$ = [];

                const dname = payload && payload.dname ? payload.dname : <string>(state.application.dataSourceRef || state.dataSourceConfig.dname);
                let dataSourceType: DataSourceType, otherDS: string;
                if (state.integration.integrationId) {
                    otherDS = (state.integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
                }
                dataSourceType = otherDS ? state.integration[otherDS].dataSourceType : state.application.dataSourceType;
                getDataSourceConfig$.push(this.applicationSettingsService.getDataSourceConfig(dataSourceType, dname));

                if (this.authenticatedUserService.isProvider() && state.application.guid && state.application.modes.length > 0) {
                    const integrationMode = state?.application?.modes ? state.application.modes[0] : state.integrationMode;
                    const defaultApplication = allowedApplications.find((application: Application) =>
                        application && state.application && (application.icon == state.application.icon)
                        && application.modes && (application.modes.indexOf(integrationMode) > -1)
                    );

                    if (defaultApplication) {
                        const providerApplicationGuid = state?.application?.guid || null;
                        const claimName = `provider.application.${integrationMode}.${defaultApplication.guid}`;
                        getDataSourceConfig$.push(this.announcementService.checkClaimsForProviderApplication(claimName, providerApplicationGuid));
                    }
                }

                return combineLatest(getDataSourceConfig$)
                    .pipe(
                        withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                        mergeMap(
                            ([configs, newState]: [any[], fromApplicationSettings.State]) => {

                                if (!newState?.application?.dataSourceRef
                                    || ((newState?.application?.type === ApplicationType.PRIVATE) && (dname === newState?.application?.dataSourceRef))
                                    || (newState?.application?.type === ApplicationType.PUBLIC)
                                ) {

                                    const dataSourceConfig = this.applicationSettingsService.modifyDataSourceConfigAfterLoad(
                                        configs[0], state.application, state.defaultApplications
                                    );

                                    const result = [];

                                    if (state.application.configurationStep === 0 || (payload && payload.stopSpinner)) {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.SET_IS_LOADING,
                                            payload: false
                                        });

                                        if (this.spinner && this.spinner.active) {
                                            this.spinner.stop();
                                        }
                                    }

                                    if (configs[1] && configs[1].claims) {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.ASSIGN_ACTUAL_PRIVACY_POLICY_DOCUMENT,
                                            payload: configs[1].claims[0]
                                        });
                                    }

                                    if ((dataSourceConfig.dataSourceType === DataSourceType.GoogleClassroom)
                                        || (dataSourceConfig.dataSourceType === DataSourceType.GSuite)) {

                                        if (!state.dataSourceConfig.isValidConfiguration
                                            && (<GoogleClassroomConfig>state.dataSourceConfig).authUrl) {
                                            (<GoogleClassroomConfig>dataSourceConfig).authUrl = null;

                                            if (dataSourceConfig.isValidConfiguration) {

                                                const dataSourceTypeTitle = DataSourceTypeTitleMap.get(dataSourceConfig.dataSourceType);
                                                const message = `Successfully authenticated to ${dataSourceTypeTitle}.`;
                                                this.snackBarService.showSnackBarMessage(message, 3000);

                                            } else {

                                                const dataSourceTypeTitle = DataSourceTypeTitleMap.get(state.dataSourceConfig.dataSourceType);
                                                const message = `Authentication to ${dataSourceTypeTitle} failed.`;
                                                this.snackBarService.showSnackBarMessage(message, 3000);
                                            }

                                            if (!state.integration.integrationId && payload.createIntegration) {
                                                result.push(<Action>{
                                                    type: ApplicationSettingsActions.CREATE_INTEGRATION,
                                                    payload: Object.assign({}, state.dataSourceConfig, dataSourceConfig)
                                                });
                                            } else {
                                                result.push(<Action>{
                                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                                    payload: false
                                                });
                                            }
                                        }
                                    }

                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                        payload: dataSourceConfig
                                    });

                                    return result;
                                }
                            }
                        ),
                        catchError(error => {
                            error.message = this.GET_APPLICATION_DATA_ERROR_MESSAGE;
                            return from([
                                {
                                    type: 'FAIL',
                                    payload: {error: error}
                                },
                                {
                                    type: ApplicationSettingsActions.SET_ERROR,
                                    payload: error
                                }
                            ]);
                        })
                    );
            })
        )
    );

    updateDataSourceConfig = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.UPDATE_DATA_SOURCE_CONFIG),
            map((action: ApplicationSettingsActions.UpdateDataSourceConfig) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.authenticatedUserService.authenticatedUser$
            ),
            concatMap(([payload, state, user]: [{ loadInterationOrgsMapping: boolean; publish: boolean; createIntegration: boolean }, fromApplicationSettings.State, User]) => {
                let data = Object.assign({}, state.dataSourceConfig);
                data = this.applicationSettingsService.modifyDataSourceConfigBeforeSave(data, state.application, state.defaultApplications);
                return this.applicationSettingsService.updateDataSourceConfig(state.application.dataSourceType, state.dataSourceConfig.objectId, data)
                    .pipe(
                        mergeMap(
                            (dataSourceConfig: DataSourceConfig) => {

                                dataSourceConfig = this.applicationSettingsService.modifyDataSourceConfigAfterLoad(
                                    dataSourceConfig, state.application, state.defaultApplications);
                                const result = [<Action>{
                                    type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                    payload: dataSourceConfig
                                }];
                                if (payload.publish) {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.PUBLISH
                                    });
                                }

                                if (payload.createIntegration) {
                                    if (dataSourceConfig.dataSourceType === DataSourceType.LTI) {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.SET_INTEGRATION_MODE,
                                            payload: IntegrationMode.EXPORT
                                        });
                                    }

                                    if (!dataSourceConfig.isValidConfiguration
                                    && ((dataSourceConfig.dataSourceType === DataSourceType.GoogleClassroom)
                                        || (dataSourceConfig.dataSourceType === DataSourceType.GSuite))) {

                                        result.push({
                                            type: ApplicationSettingsActions.GET_GOOGLE_AUTH_URL
                                        });

                                        ApplicationSettingsService.setItemToLocalStorage(
                                            DATA_SOURCE_CONFIG_GOOGLE_OBJECT_ID,
                                            dataSourceConfig.objectId.toString()
                                        );
                                    } else {

                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.CREATE_INTEGRATION,
                                            payload: Object.assign({}, state.dataSourceConfig, dataSourceConfig)
                                        });
                                    }

                                } else {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.SET_IS_LOADING,
                                        payload: false
                                    });
                                }

                                if (payload.loadInterationOrgsMapping
                                && (((state.integrationMode === IntegrationMode.IMPORT) || (state.integrationMode === IntegrationMode.SYNC))
                                    || ((this.authenticatedUserService.isProvider(user) || this.authenticatedUserService.isSysAdmin(user))
                                        && (state.integrationMode === IntegrationMode.EXPORT)))) {
                                    result.push(
                                        <Action>{
                                            type: ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING,
                                            payload: {stopSpinner: false, showErrorMessage: true, messageAfterFinish: ''}
                                        },
                                        <Action>{
                                            type: ApplicationSettingsActions.SET_IS_LOADING,
                                            payload: false
                                        }
                                    );
                                }

                                if (result.length > 0) {
                                    return result;
                                }

                                return observableOf();
                            }
                        ),
                        catchError(error => {
                            const result = { type: 'FAIL', payload: {error: error}};

                            // if error, then we replace Orgs Mapping with old values from store in order to stop spinner in Orgs mapping tab
                            if (payload.loadInterationOrgsMapping
                            && state.integrationMode === IntegrationMode.IMPORT) {

                                return observableOf(
                                    result,
                                    {
                                        type: ApplicationSettingsActions.SET_INTEGRATION_ORG_MAPPING,
                                        payload: {organizationsMapping: state.integration.organizationsMappingModel}
                                    }
                                );
                            }

                            return observableOf(result);
                        })
                    );
            })
        )
    );

    createDataSourceConfigAndIntegration = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CREATE_DATA_SOURCE_CONFIG_AND_INTEGRATION),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([payload, state]) => {
                let data = Object.assign({}, state.dataSourceConfig);
                data = this.applicationSettingsService.modifyDataSourceConfigBeforeSave(data, state.application, state.defaultApplications);
                const dataSourceType = state.dataSourceConfig.dataSourceType || state.application.dataSourceType;
                return this.applicationSettingsService.createDataSourceConfig(dataSourceType, data)
                    .pipe(
                        mergeMap(
                            (dataSourceConfig) => {
                                const bindDataSourceToApplicationData = {
                                    dataSourceRef: dataSourceConfig.dname,
                                    dataSourceType: dataSourceConfig.dataSourceType,
                                    modes: [state.integrationMode]
                                };
                                dataSourceConfig = this.applicationSettingsService.modifyDataSourceConfigAfterLoad(
                                    dataSourceConfig, state.application, state.defaultApplications);

                                const result = [<Action>{
                                    type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                    payload: dataSourceConfig
                                }];

                                if (!dataSourceConfig.isValidConfiguration
                                && ((dataSourceConfig.dataSourceType === DataSourceType.GoogleClassroom)
                                    || (dataSourceConfig.dataSourceType === DataSourceType.GSuite))) {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.GET_GOOGLE_AUTH_URL
                                    });
                                    ApplicationSettingsService.setItemToLocalStorage(
                                        DATA_SOURCE_CONFIG_GOOGLE_OBJECT_ID,
                                        dataSourceConfig.objectId.toString()
                                    );
                                } else {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.CREATE_INTEGRATION,
                                        payload: Object.assign({}, state.dataSourceConfig, dataSourceConfig)
                                    });
                                }

                                if (state.application.guid && !state.application.dataSourceRef) {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.BIND_DATA_SOURCE_TO_APPLICATION,
                                        payload: bindDataSourceToApplicationData
                                    });
                                }

                                if (dataSourceConfig.dataSourceType === DataSourceType.LTI) {
                                    result.unshift(<Action>{
                                        type: ApplicationSettingsActions.SET_INTEGRATION_MODE,
                                        payload: IntegrationMode.EXPORT
                                    });
                                }

                                return result;
                            }
                        ),
                        catchError(error => {
                            const message = this.SAVE_DELIVERY_CONFIG_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    saveMappingRulesArchiveOU = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_MAPPING_RULES_ARCHIVE_OU),
            map((action: ApplicationSettingsActions.SaveMappingRulesArchiveOU) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([archiveOU, state]: [string, fromApplicationSettings.State]) =>
                this.applicationSettingsService.saveMappingRulesArchiveOU(
                    state.dataSourceConfig.dataSourceType,
                    state.dataSourceConfig.objectId,
                    archiveOU,
                    state.application.ownerId
                ).pipe(
                    mergeMap((dataSourceConfig: DataSourceConfig) =>
                        [{
                            type: ApplicationSettingsActions.ASSIGN_MAPPING_RULES_ARCHIVE_OU,
                            payload: dataSourceConfig['archiveOu']
                        }]
                    ),
                    catchError(error => {
                        const message = this.SAVE_MAPPING_RULES_ARCHIVE_OU_ERROR_MESSAGE;
                        this.snackBarService.showSnackBarMessage(message, 3000);
                        return observableOf({type: 'FAIL', payload: {error: error}});
                    })
                )
            ))
    );

    createSharedPool = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CREATE_SHARED_POOL),
            map((action: ApplicationSettingsActions.CreateSharedPool) => action.payload),
            concatMap((payload: SharedPoolResponseData) => {

                return this.applicationSettingsService.createSharedPool(payload)
                    .pipe(
                        mergeMap(
                            (defaultPool: SharedPoolResponseData) => {

                                return [{
                                    type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                    payload: {
                                        createSharedPool: defaultPool.includeAllDistricts,
                                        defaultPoolId: defaultPool ? defaultPool.guid : null
                                    }
                                }];
                            }
                        ),
                        catchError(error => {
                            const message = this.SAVE_DELIVERY_CONFIG_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    getSharedPoolsByApp = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_SHARED_POOLS_BY_APP),
            map((action: ApplicationSettingsActions.GetSharedPoolsByApp) => action.payload),
            concatMap((payload: string) => {

                return this.applicationSettingsService.getSharedPoolsByApp(payload)
                    .pipe(
                        mergeMap(
                            (response: SharedPoolResponseData[]) => {

                                const defaultPool = response.find((item: SharedPoolResponseData) => item.includeAllDistricts);

                                return [{
                                    type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                    payload: {
                                        createSharedPool: !!defaultPool,
                                        defaultPoolId: defaultPool ? defaultPool.guid : null
                                    }
                                }];
                            }
                        ),
                        catchError(error => {
                            const message = this.GET_APPLICATION_DATA_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    bindDataSourceToApplication = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.BIND_DATA_SOURCE_TO_APPLICATION),
            map((action: ApplicationSettingsActions.BindDataSourceToApplication) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([params, state]) => {
                const body = Object.assign(state.application, {guid: state.application.guid, dname: state.dataSourceConfig.dname}, params);
                return this.applicationSettingsService.bindDataSourceToApplication(body)
                    .pipe(
                        map((application: Application) => ({
                                type: ApplicationSettingsActions.ASSIGN_APPLICATION,
                                payload: application
                            })
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    );
            })
        )
    );

    createIntegration = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CREATE_INTEGRATION),
            concatMap(() => this.applicationSettingsService.getConfiguredEdutoneDataSources()
                .pipe(
                    map((dataSourceConfig: DataSourceConfig[]) => {
                            const edutoneDataSource = dataSourceConfig.filter(dataSource =>
                                (dataSource.dataSourceType === DataSourceType.EduTone) && !dataSource.dname.endsWith('_DEFAULT'));
                            return edutoneDataSource[0];
                        }
                    ),
                    withLatestFrom(
                        this.store.pipe(select(getApplicationSettings)),
                        this.store.pipe(select(getSchoolYearRolloverSettings)),
                        this.store.pipe(select(getProviderApplications)),
                        this.authenticatedUserService.authenticatedUser$
                    ),
                    concatMap(([edutoneDataSource, state, schoolYearRolloverSettings, applications, user]
                                   : [DataSourceConfig, fromApplicationSettings.State, ISchoolYearRolloverSettings, ApplicationProviderListModel[], User]) => {
                        let source: DataSourceConfig, target: DataSourceConfig, integrationState: IntegrationState;
                        if (state.integrationMode === IntegrationMode.EXPORT) {
                            source = edutoneDataSource;
                            target = state.dataSourceConfig;
                        } else {
                            source = state.dataSourceConfig;
                            target = edutoneDataSource;
                        }
                        const body: Integration = <Integration>{
                            title: 'Application Title',
                            mode: state.integrationMode,
                            source: source,
                            target: target
                        };

                        integrationState = state.integrationState;
                        if (state.integrationMode === IntegrationMode.IMPORT || state.integrationMode === IntegrationMode.SYNC) {
                            const isSuspendDataImport = ApplicationSettingsService.isSuspendDataImportDueRolloverPeriod(
                                schoolYearRolloverSettings);
                            const isDetectedAppWithSignificantChanges = applications && applications.find((application: ApplicationProviderListModel) =>
                                <IntegrationState>application.state === IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_CURRENT);

                            if (isSuspendDataImport) {
                                integrationState = IntegrationState.SUSPENDED_DUE_SCHOOL_YEAR_ROLL_OVER_PERIOD;
                            } else if (isDetectedAppWithSignificantChanges) {
                                integrationState = IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_OTHER;
                            }
                        }

                        return this.applicationSettingsService.createIntegration(state.application.guid, integrationState, body)
                            .pipe(
                                mergeMap((integration: Integration) => {
                                        const result = [
                                            <Action>{
                                                type: ApplicationSettingsActions.SET_INTEGRATION_STATE,
                                                payload: integration.state
                                            },
                                            <Action>{
                                                type: ApplicationSettingsActions.SET_INTEGRATION,
                                                payload: integration
                                            }
                                        ];

                                        if (state.application.guid) {
                                        result.push(
                                            <Action>{
                                                type: ApplicationSettingsActions.GET_INTEGRATION_RULES,
                                                payload: integration.integrationId
                                            },
                                            <Action>{
                                                type: ApplicationSettingsActions.GET_PREDICATES_METADATA
                                            }
                                        );

                                        if ((integration.mode === IntegrationMode.IMPORT)
                                                || (integration.mode === IntegrationMode.SYNC)) {
                                            result.push(<Action>{type: ApplicationSettingsActions.GET_MERGING_RULES});
                                            result.push(new IntegrationActions.GetProviderApplicationsAction());
                                            result.push(<Action>{
                                                type: ApplicationSettingsActions.GET_MASTER_FIELDS,
                                                payload: state.application.ownerId
                                            });
                                        }

                                    } else {
                                            result.push(<Action>{
                                                type: ApplicationSettingsActions.SET_IS_LOADING,
                                                payload: false
                                            });
                                    }

                                    if (integration.hasMultipleOrgs && state.application.guid) {
                                        if (!(state.application.configurationStep < APP_LAST_STEP
                                                && integration.source.friendlyName === DataSourceFriendlyName['CSV Upload'])
                                        ) {
                                            result.push(<Action>{
                                                type: ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING,
                                                payload: {stopSpinner: false, showErrorMessage: true, messageAfterFinish: ''}
                                            });
                                        }
                                    }

                                    if (state.googleClassroomDSOptions?.isGoogleClassroomReloadCoursesFeatureActive
                                            && (state.dataSourceConfig.dataSourceType === DataSourceType.GoogleClassroom)
                                            && state.application.modes
                                            && (state.application.modes.includes(IntegrationMode.EXPORT)
                                                || state.application.modes.includes(IntegrationMode.SYNC))
                                            && state.dataSourceConfig.isValidConfiguration) {

                                        result.push(
                                            <Action>{
                                                type: ApplicationSettingsActions.RELOAD_GOOGLE_CLASSROOM_COURSES
                                            },
                                            <Action>{
                                                type: ApplicationSettingsActions.SET_GOOGLE_CLASSROOM_DATA_SOURCE_OPTIONS,
                                                payload: {isLoadingGoogleClassroomCourses: true}
                                            }
                                        );
                                    }

                                        // Set Gradebook feature toggles for Powerschool on creating and get afterwards once more Integration with gradebook tasks (don not get Orgs Mapping again).
                                        const isGradeBookSyncFeatureToggleAutoTurnedOnByDefault =
                                            ApplicationSettingsService.getIsGradeBookSyncFeatureToggleAutoTurnedOnByDefault(
                                                state.allowedApplications,
                                                state.application,
                                                integration?.mode,
                                                this.authenticatedUserService.isProvider(user)
                                            );
                                        if (isGradeBookSyncFeatureToggleAutoTurnedOnByDefault) {
                                            this.featureTogglesService.saveFeatureToggles$.next(true);
                                        }

                                        return result;
                                    }
                                ),
                                catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                            );
                    }),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    getIntegrationData = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_INTEGRATION_DATA),
            map((action: ApplicationSettingsActions.GetIntegrationData) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.authenticatedUserService.authenticatedUser$
            ),
            concatMap(([{ownerGuid, getInterationOrgsMapping}, state, user]
                           : [{ ownerGuid: string; getInterationOrgsMapping: boolean }, fromApplicationSettings.State, User]) =>
                this.applicationSettingsService.getIntegrationData(
                    state.application.guid,
                    ownerGuid,
                    state.userPermission.isPermittedToEditVendorDefaultApplication
                )
                    .pipe(
                        withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                        mergeMap(
                            ([integration, newState]: [Integration, fromApplicationSettings.State]) => {

                                if (!newState?.application?.guid || (integration?.applicationId === newState?.application?.guid)) {

                                    const result = [
                                        <Action>{
                                            type: ApplicationSettingsActions.SET_INTEGRATION_STATE,
                                            payload: integration.state
                                        },
                                        <Action>{
                                            type: ApplicationSettingsActions.SET_INTEGRATION_MODE,
                                            payload: integration.mode
                                        },
                                        <Action>{
                                            type: ApplicationSettingsActions.SET_INTEGRATION,
                                            payload: integration
                                        }
                                    ];

                                    if (state.application.guid) {
                                        result.push(
                                            <Action>{
                                                type: ApplicationSettingsActions.GET_INTEGRATION_RULES,
                                                payload: integration.integrationId
                                            },
                                            <Action>{
                                                type: ApplicationSettingsActions.GET_PREDICATES_METADATA
                                            }
                                        );
                                    }

                                    if (integration.hasMultipleOrgs) {
                                        const isGradeBookSyncFeatureToggleAutoTurnedOnByDefault =
                                            ApplicationSettingsService.getIsGradeBookSyncFeatureToggleAutoTurnedOnByDefault(
                                                state.allowedApplications,
                                                state.application,
                                                integration?.mode,
                                                this.authenticatedUserService.isProvider(user)
                                            );
                                        const isCreatingDSGradeBookSyncFeatureToggleAutoTurnedOnByDefault =
                                            isGradeBookSyncFeatureToggleAutoTurnedOnByDefault
                                            && newState.integration?.integrationId
                                            && (newState.integration.tasks.length < integration.tasks.length)
                                            && (newState.integration?.organizationsMappingModel?.sourceOrganizations?.length > 0);

                                        const isCreatingCSVUpload = ((state.application.configurationStep < APP_LAST_STEP)
                                            && (integration.source.friendlyName === DataSourceFriendlyName['CSV Upload']));

                                        if (!isCreatingCSVUpload
                                            && ((state.application.ownerId === user.orgGuid) || getInterationOrgsMapping)
                                            && !isCreatingDSGradeBookSyncFeatureToggleAutoTurnedOnByDefault
                                        ) {
                                            result.push(<Action>{
                                                type: ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING,
                                                payload: {stopSpinner: false, showErrorMessage: true, messageAfterFinish: ''}
                                            });
                                        }

                                        // Set Gradebook feature toggles for Powerschool on creating and get afterwards once more Integration with gradebook tasks (don not get Orgs Mapping again).
                                        if (isCreatingDSGradeBookSyncFeatureToggleAutoTurnedOnByDefault) {
                                            integration.organizationsMappingModel = newState.integration.organizationsMappingModel;
                                        }
                                    }

                                    if ((state.application.ownerId !== user.orgGuid) && !getInterationOrgsMapping) {
                                        if (this.spinner && this.spinner.active) {
                                            this.spinner.stop();
                                        }

                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.SET_IS_LOADING,
                                            payload: false
                                        });
                                    }

                                    return result;
                                }
                        }
                    ),
                    catchError(error => {
                        error.message = this.GET_APPLICATION_DATA_ERROR_MESSAGE;
                        return from([
                            {
                                type: 'FAIL',
                                payload: {error: error}
                            },
                            {
                                type: ApplicationSettingsActions.SET_ERROR,
                                payload: error
                            }
                        ]);
                    })
                    ))
        )
    );

    getApplicationIntegration = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_APPLICATION_INTEGRATION),
            map((action: ApplicationSettingsActions.GetApplicationIntegration) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([payload, oldState]:
                           [{ appGuid: string; ownerGuid: string; getInterationOrgsMapping: boolean, withAttributes: boolean, getIntegrationRulesData: boolean },
                               fromApplicationSettings.State]) =>
                this.integrationService.getIntegration(payload.appGuid, payload.ownerGuid, payload.withAttributes)

                    .pipe(
                        withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                        mergeMap(
                            ([integration, newState]: [Integration, fromApplicationSettings.State]) => {

                                const isIntegrationStateChanged = (integration?.state !== newState?.integration?.state);

                                if (!newState?.application?.guid
                                    || (integration?.applicationId === newState?.application?.guid)
                                    || isIntegrationStateChanged
                                ) {
                                    const isInitialSetIntegration = !newState.integration?.integrationId;
                                    const needToUpdateIntegrationTasksData = ApplicationSettingsService.needToUpdateIntegrationTasksData(
                                        newState.integration, integration);

                                    const result = [
                                        <Action>{
                                            type: ApplicationSettingsActions.SET_INTEGRATION_STATE,
                                            payload: integration.state
                                        },
                                        <Action>{
                                            type: ApplicationSettingsActions.SET_INTEGRATION_MODE,
                                            payload: integration.mode
                                        },
                                    ];

                                    if (isInitialSetIntegration || isIntegrationStateChanged) {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.SET_INTEGRATION,
                                            payload: integration
                                        });

                                        if (payload.getIntegrationRulesData) {
                                            result.push(<Action>{
                                                    type: ApplicationSettingsActions.GET_INTEGRATION_RULES,
                                                    payload: integration.integrationId
                                                },
                                                {
                                                    type: ApplicationSettingsActions.GET_PREDICATES_METADATA
                                                });
                                        }
                                    }

                                    if (needToUpdateIntegrationTasksData) {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.ASSIGN_INTEGRATION_TASKS,
                                            payload: integration.tasks
                                        });
                                    }

                                    if (integration.hasMultipleOrgs && payload.getInterationOrgsMapping) {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING,
                                            payload: {stopSpinner: false, showErrorMessage: true, messageAfterFinish: ''}
                                        });
                                    }

                                    return result;
                                }
                            }
                        ),
                        catchError(error => {
                            if (typeof error === 'string') {
                                error = new Error(error);
                            } else {
                                error.message = this.GET_APPLICATION_DATA_ERROR_MESSAGE;
                            }

                            return from([
                                {
                                    type: 'FAIL',
                                    payload: {error: error}
                                },
                                {
                                    type: ApplicationSettingsActions.SET_ERROR,
                                    payload: error
                                },
                                {
                                    type: ApplicationSettingsActions.SET_INTEGRATION,
                                    payload: Object.assign({}, oldState.integration, {failedToGetIntegration: true})
                                }
                            ]);
                        })
                    ))
        )
    );

    getFTPConfig = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_FTP_CONFIG),
            map((action: ApplicationSettingsActions.GetFTPConfig) => action.payload),
            concatMap((providerGuid: string) => this.applicationSettingsService.getFTPConfig(providerGuid)
                .pipe(
                    map(
                        (ftpConfig: FTPConfig) => {
                            if (!ftpConfig.userPermissions) {
                                return {
                                    type: ApplicationSettingsActions.FAIL,
                                    payload: {error: new Error()}
                                };
                            }
                            return {
                                type: ApplicationSettingsActions.SET_FTP_CONFIG,
                                payload: ftpConfig
                            };
                        }
                    ),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    createFTPUser = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CREATE_FTP_USER),
            map((action: ApplicationSettingsActions.CreateFTPUser) => action.payload),
            switchMap(({
                           ftpUser,
                           providerGuid
                       }: { ftpUser: FTPUser; providerGuid: string }) => this.applicationSettingsService.createFTPUser(ftpUser, providerGuid)
                .pipe(
                    map(
                        (responseFTPUser: FTPUser) => ({
                            type: ApplicationSettingsActions.ASSIGN_FTP_CONFIG,
                            payload: {ftpUser: responseFTPUser, oldUsername: null}
                        })
                    ),
                    catchError(error => {
                        if (error.status === 406) {
                            const message = 'Username is already taken. Please try another.';
                            this.snackBarService.showSnackBarMessage(message, 3000);
                        }
                        return observableOf({type: 'SET_IS_SAVING_FTP_USER', payload: false});
                    })
                ))
        )
    );

    editFTPUser = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.EDIT_FTP_USER),
            map((action: ApplicationSettingsActions.EditFTPUser) => action.payload),
            switchMap(({
                           ftpUser,
                           oldUsername,
                           providerGuid
                       }: { ftpUser: FTPUser; oldUsername: string; providerGuid: string }) => this.applicationSettingsService.updateFTPUser(ftpUser, providerGuid, oldUsername)
                .pipe(
                    map(
                        (responseFTPUser: FTPUser) => ({
                            type: ApplicationSettingsActions.ASSIGN_FTP_CONFIG,
                            payload: {ftpUser: responseFTPUser, oldUsername: oldUsername}
                        })
                    ),
                    catchError(error => {
                        if (error.status === 406) {
                            const message = error.error;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                        }
                        return observableOf({type: 'SET_IS_SAVING_FTP_USER', payload: false});
                    })
                ))
        )
    );

    deleteFTPUser = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.DELETE_FTP_USER),
            map((action: ApplicationSettingsActions.DeleteFTPUser) => action.payload),
            switchMap(({
                           username,
                           providerGuid
                       }: { username: string; providerGuid: string }) => this.applicationSettingsService.deleteFTPUser(username, providerGuid)
                .pipe(
                    map(
                        () => ({
                            type: ApplicationSettingsActions.ASSIGN_FTP_CONFIG,
                            payload: {ftpUser: null, oldUsername: username}
                        })
                    ),
                    catchError((error: any): Observable<any> => {
                        if (error.status === 200 && error.error.text === 'deleted') {
                            return observableOf({
                                type: ApplicationSettingsActions.ASSIGN_FTP_CONFIG,
                                payload: {ftpUser: null, oldUsername: username}
                            });
                        }
                        return observableOf({type: 'FAIL', payload: {error: error}});
                    })
                ))
        )
    );

    getRankOneDistrictList = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_RANK__ONE_DISTRICT_LIST),
            concatMap(() => this.http.get<RankOneDistrictItem[]>(`${environment.datahubBaseURI}/api/v2/applications/sftp/rankone/districts`)
                .pipe(
                    map(
                        (rankOneDistrictList: RankOneDistrictItem[]) => ({
                            type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                            payload: {rankOneDistrictList: rankOneDistrictList}
                        })
                    ),
                    catchError(error => observableOf(
                        {
                            type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                            payload: {rankOneDistrictList: error}
                        }
                    ))
                ))
        )
    );

    checkExistanceOfPowerSchoolPlugin = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CHECK_EXISTANCE_OF_POWER_SCHOOL_PLUGIN),
            map((action: ApplicationSettingsActions.CheckExistanceOfPowerSchoolPlugin) => action),
            concatMap((action) => this.http.get<boolean>(`${environment.datahubBaseURI}/api/v2/applications/power-school/configuration/${action.payload}/plugin/check`)
                .pipe(
                    map((data: boolean) => ({
                            type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                            payload: {powerSchoolPluginExist: data}
                        })
                    ),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    createDataSourceConfigWithoutCreatingIntegration = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CREATE_DATA_SOURCE_CONFIG_WITHOUT_CREATING_INTEGRATION),
            map((action: ApplicationSettingsActions.CreateDataSourceConfigWithoutCreatingIntegration) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([payload, state]: [{ actionToDispatch: string; step: number }, fromApplicationSettings.State]) => {
                // we don't save defaultSchemaName
                let data = Object.assign({}, state.dataSourceConfig, {defaultSchemaName: ''});
                data = this.applicationSettingsService.modifyDataSourceConfigBeforeSave(data, state.application, state.defaultApplications);
                return this.applicationSettingsService.createDataSourceConfig(state.application.dataSourceType, data)
                    .pipe(
                        mergeMap((dataSourceConfig: DataSourceConfig) => {
                            const bindDataSourceToApplicationData: any = {
                                dataSourceRef: dataSourceConfig.dname,
                                dataSourceType: dataSourceConfig.dataSourceType,
                                modes: [state.integrationMode]
                            };

                                if (payload.step) {
                                    bindDataSourceToApplicationData.configurationStep = payload.step;
                                }

                                dataSourceConfig = this.applicationSettingsService.modifyDataSourceConfigAfterLoad(
                                    dataSourceConfig, state.application, state.defaultApplications);
                                // we don't change selected defaultSchemaName
                                dataSourceConfig.defaultSchemaName = state.dataSourceConfig.defaultSchemaName;

                                const result = <Action[]>[
                                    <Action>{
                                        type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                        payload: dataSourceConfig
                                    },
                                    <Action>{
                                        type: ApplicationSettingsActions.BIND_DATA_SOURCE_TO_APPLICATION,
                                        payload: bindDataSourceToApplicationData
                                    }
                                ];

                                if ((dataSourceConfig.dataSourceType === DataSourceType.GoogleClassroom)
                                || (dataSourceConfig.dataSourceType === DataSourceType.GSuite)) {

                                ApplicationSettingsService.setItemToLocalStorage(
                                    DATA_SOURCE_CONFIG_GOOGLE_OBJECT_ID,
                                    dataSourceConfig.objectId.toString()
                                );
                            }

                            if (payload.actionToDispatch) {
                                result.push(<Action>{
                                    type: payload.actionToDispatch
                                });
                            }

                                return from(result);
                            }
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    );
            })
        )
    );

    generateSecretKey = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GENERATE_SECRET_KEY),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]) => {
                let dataSourceConfig = Object.assign({}, state.dataSourceConfig);
                dataSourceConfig = this.applicationSettingsService.modifyDataSourceConfigBeforeSave(dataSourceConfig, state.application, state.defaultApplications);
                return this.applicationSettingsService.generateSecretKey(dataSourceConfig)
                    .pipe(
                        map(
                            (data: { encryptSecretKeyTimestamp: Date; encryptSecretKey: string }) => {
                                const message = 'Secret key successfully generated';
                                this.snackBarService.showSnackBarMessage(message, 1500);
                                return {
                                    type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                    payload: data
                                };
                            }
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    );
            })
        )
    );

    generateLTICredentials = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GENERATE_LTI_CREDENTIALS),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]) => {
                const dataSourceConfig: LTIConfig = <LTIConfig>Object.assign({}, state.dataSourceConfig);
                const modifiedDataSourceConfig = <LTIConfig>this.applicationSettingsService.modifyDataSourceConfigBeforeSave(
                    dataSourceConfig, state.application, state.defaultApplications);
                return this.applicationSettingsService.generateLTICredentials(modifiedDataSourceConfig)
                    .pipe(
                        mergeMap(
                            (dsConfig: LTIConfig) => {
                                const message = 'Credentials were successfully generated.';
                                this.snackBarService.showSnackBarMessage(message, 1500);
                                return [{
                                    type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                    payload: dsConfig
                                }];
                            }
                        ),
                        catchError(error => {
                            const message = this.GENERATE_LTI_CREDENTIALS_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);

                            return observableOf(
                                {
                                    type: 'FAIL',
                                    payload: {error: error}
                                },
                                {
                                    type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                    payload: {getLTICredentialsError: true}
                                });
                        })
                    );
            })
        )
    );

    getGoogleAuthUrl = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_GOOGLE_AUTH_URL),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]: [any, fromApplicationSettings.State]) => this.applicationSettingsService.getGoogleAuthUrl(state.dataSourceConfig.dataSourceType, state.integrationMode)
                .pipe(
                    mergeMap(
                        (authUrl: unknown) => [{
                            type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                            payload: {authUrl: authUrl}
                        }]
                    ),
                    catchError((error: unknown) => {
                        const dataSourceTypeTitle = DataSourceTypeTitleMap.get(state.dataSourceConfig.dataSourceType);
                        const message = `Unknown error occurred while authenticating to ${dataSourceTypeTitle}.`;
                        this.snackBarService.showSnackBarMessage(message, 3000);
                        const updatedStep = (state.application.configurationStep - 1) > 0
                            ? state.application.configurationStep - 1
                            : 0;

                        return observableOf(
                            {
                                type: 'FAIL',
                                payload: {error: error}
                            },
                            {
                                type: ApplicationSettingsActions.ASSIGN_APPLICATION,
                                payload: {configurationStep: updatedStep}
                            }
                        );
                    })
                ))
        )
    );

    revokeAccess = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.REVOKE_ACCESS),
            map((action: ApplicationSettingsActions.RevokeAccess) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([objectId, state]) => this.applicationSettingsService.revokeAccess(objectId || state.dataSourceConfig.objectId, state.dataSourceConfig.dataSourceType)
                .pipe(
                    mergeMap(
                        (dataSourceConfig: DataSourceConfig) => {

                            if ((dataSourceConfig.dataSourceType === DataSourceType.GoogleClassroom)
                                || (dataSourceConfig.dataSourceType === DataSourceType.GSuite)) {
                                const message = this.SUCCESS_REVOKE_ACCESS;
                                this.snackBarService.showSnackBarMessage(message, 3000);
                            }

                            return [{
                                type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                payload: dataSourceConfig
                            }];
                        }
                    ),
                    catchError((error: unknown) => {
                        const message = this.UNSUCCESS_REVOKE_ACCESS;
                        this.snackBarService.showSnackBarMessage(message, 3000);
                        return observableOf({
                            type: 'FAIL',
                            payload: {error: error}
                        });
                    })
                ))
        )
    );

    reloadGoogleClassroomCourses = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.RELOAD_GOOGLE_CLASSROOM_COURSES),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]: [any, fromApplicationSettings.State]) => this.applicationSettingsService.reloadGoogleClassroomCourses(state.dataSourceConfig.dname)
                .pipe(
                    mergeMap(
                        () => [{
                            type: ApplicationSettingsActions.GET_IS_LOADING_GOOGLE_CLASSROOM_COURSES_VALUE
                        }]
                    ),
                    catchError((error: unknown) => {
                        const message = `Unknown error occurred while reloading courses.`;
                        this.snackBarService.showSnackBarMessage(message, 3000);

                        return observableOf(
                            {
                                type: 'FAIL',
                                payload: {error: error}
                            },
                            {
                                type: ApplicationSettingsActions.SET_GOOGLE_CLASSROOM_DATA_SOURCE_OPTIONS,
                                payload: {isLoadingGoogleClassroomCourses: false}
                            }
                        );
                    })
                ))
        )
    );

    getIsLoadingGoogleClassroomCoursesValue = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_IS_LOADING_GOOGLE_CLASSROOM_COURSES_VALUE),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]: [any, fromApplicationSettings.State]) =>
                this.applicationSettingsService.getIsLoadingGoogleClassroomCourses(state.application.dataSourceRef)
                    .pipe(
                        delay(1000),
                        mergeMap(
                            (isLoadingGoogleClassroomCourses: boolean) => {
                                const result = [];

                                if (isLoadingGoogleClassroomCourses !== state.googleClassroomDSOptions.isLoadingGoogleClassroomCourses) {
                                    result.push({
                                        type: ApplicationSettingsActions.SET_GOOGLE_CLASSROOM_DATA_SOURCE_OPTIONS,
                                        payload: {isLoadingGoogleClassroomCourses: isLoadingGoogleClassroomCourses}
                                    });
                                }

                                if (isLoadingGoogleClassroomCourses) {
                                    result.push({
                                        type: ApplicationSettingsActions.GET_IS_LOADING_GOOGLE_CLASSROOM_COURSES_VALUE
                                    });
                                }

                                if (!isLoadingGoogleClassroomCourses && state.googleClassroomDSOptions.isLoadingGoogleClassroomCourses) {
                                    result.push({
                                        type: ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING,
                                        payload: {
                                            stopSpinner: false,
                                            showErrorMessage: true,
                                            messageAfterFinish: 'Reloading Courses is completed - now you can Edit and Save Schools Mapping.'
                                        }
                                    });
                                }

                                return result;
                            }
                        ),
                        catchError((error: unknown) => {
                            const result = {type: 'FAIL', payload: {error: error}};

                            if (state.googleClassroomDSOptions.isLoadingGoogleClassroomCourses) {
                                const message = `Unknown error occurred while reloading courses.`;
                                this.snackBarService.showSnackBarMessage(message, 3000);

                                return observableOf(
                                    result,
                                    {
                                        type: 'FAIL',
                                        payload: {error: error}
                                    });
                            }

                            return observableOf(result);
                        })
                    )
            )
        )
    );

    testConnection = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.TEST_CONNECTION),
            map((action: ApplicationSettingsActions.TestConnection) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([{
                dataSourceConfig,
                isTestingConnectionSubject$,
                isTestingETXLDAPConnectionSubject$,
                isValidConnection$,
                isValidETXLDAPConnection$,
                onTestConnectionForDebug$,
                updateDataSourceConfig,
                actionToDispatch
            }, state]: [
                {
                    dataSourceConfig: DataSourceConfig;
                    isTestingConnectionSubject$: BehaviorSubject<boolean>;
                    isTestingETXLDAPConnectionSubject$: BehaviorSubject<boolean>;
                    isValidConnection$: BehaviorSubject<boolean>;
                    isValidETXLDAPConnection$: BehaviorSubject<boolean>;
                    onTestConnectionForDebug$: BehaviorSubject<DataSourceConfig>;
                    updateDataSourceConfig: boolean;
                    actionToDispatch?: UpdateDataSourceConfig;
                }, fromApplicationSettings.State]) => {

                let data = Object.assign({}, dataSourceConfig);

                if (state.application && state.application.modes && (state.application.modes.length > 0)) {
                    data.mode = state.application.modes[0];
                }

                data = this.applicationSettingsService.modifyDataSourceConfigBeforeSave(data, state.application, state.defaultApplications);
                return this.applicationSettingsService.testConnection(data.dataSourceType || state.application.dataSourceType, data)
                    .pipe(
                        mergeMap((response: HttpResponse<string>) => {
                            isTestingConnectionSubject$.next(false);
                            let isTestingETXLDAPConnection: boolean;
                            let isValidETXLDAPConnection: boolean;

                            if (isTestingETXLDAPConnectionSubject$) {
                                isTestingETXLDAPConnectionSubject$.pipe(take(1)).subscribe((isTestingETXLDAPConnectionValue: boolean) => {
                                    isTestingETXLDAPConnection = isTestingETXLDAPConnectionValue;
                                });

                                isValidETXLDAPConnection$.pipe(take(1)).subscribe((isValidETXLDAPConnectionValue: boolean) => {
                                    isValidETXLDAPConnection = isValidETXLDAPConnectionValue;
                                });
                            }

                            const isValidConnectConnection = (response.status === 200)
                                && (!response.body
                                    || (response.body && (<any>response.body).status && ((<any>response.body).status === 200)));
                            isValidConnection$.next(isValidConnectConnection);

                            const dataSourceConfigAssignPart = {
                                isValidConfiguration: isValidConnectConnection,
                                notValidConfigurationMessage: ''
                            };

                            if (isValidConnectConnection) {

                                if (isValidETXLDAPConnection$) {
                                    if (!isTestingETXLDAPConnection) {
                                        if (isValidETXLDAPConnection) {
                                            this.snackBarService.showSnackBarMessage('Connection is ok.', 3000);
                                        } else {
                                            if (isValidETXLDAPConnection === false) {
                                                this.snackBarService.showSnackBarMessage('Failed to establish connection to authentication server.', 5000);
                                            }
                                        }
                                    }
                                } else {
                                    this.snackBarService.showSnackBarMessage('Connection is ok.', 3000);
                                }

                            } else {

                                if (response.body && (<any>response.body).status && ((<any>response.body).status === 500)) {
                                    dataSourceConfigAssignPart.notValidConfigurationMessage = 'Public keyset URL is invalid';
                                } else {
                                    dataSourceConfigAssignPart.notValidConfigurationMessage = 'Failed to establish connection';
                                }

                                const snackBarRef$: Subject<MatSnackBarRef<TestConnectionSnackbarComponent>> =
                                    this.snackBarService.onOpenFromComponent(
                                        7000,
                                        TestConnectionSnackbarComponent,
                                        {
                                            message: dataSourceConfigAssignPart.notValidConfigurationMessage,
                                            debugMode: false && true
                                        }
                                    );

                                snackBarRef$.subscribe({
                                    next: (snackBarRef: MatSnackBarRef<TestConnectionSnackbarComponent>) => {
                                        if (snackBarRef && ApplicationSettingsService.isTestConnectionForDebugEnabled(dataSourceConfig)) {
                                            snackBarRef.onAction().pipe(take(1))
                                                .subscribe(() => {
                                                    onTestConnectionForDebug$.next(data);
                                                });
                                        }
                                    }
                                });
                            }

                            const result = <Action[]>[];
                            result.push(<Action>{
                                type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                payload: dataSourceConfigAssignPart
                            });

                            if (updateDataSourceConfig
                                && (dataSourceConfigAssignPart.isValidConfiguration !== state.dataSourceConfig.isValidConfiguration)
                                && state.dataSourceConfig.objectId) {
                                result.push(<Action>{
                                    type: ApplicationSettingsActions.UPDATE_DATA_SOURCE_CONFIG,
                                    payload: {loadInterationOrgsMapping: false, publish: false}
                                });
                            }

                            if (actionToDispatch) {
                                result.push(actionToDispatch);
                            }

                            return result;
                        }),
                        catchError(error => {
                            isTestingConnectionSubject$.next(false);
                            isValidConnection$.next(false);
                            const isValidConfiguration = false;
                            const notValidConfigurationMessage = (error.error === 'Coudn`t create folder, permission denied')
                                ? 'Coudn`t create folder, permission denied'
                                : 'Failed to establish connection';

                            const snackBarRef$: Subject<MatSnackBarRef<TestConnectionSnackbarComponent>> =
                                this.snackBarService.onOpenFromComponent(
                                    7000,
                                    TestConnectionSnackbarComponent,
                                    {
                                        message: notValidConfigurationMessage,
                                        debugMode: false && true
                                    }
                                );

                            snackBarRef$.subscribe({
                                next: (snackBarRef: MatSnackBarRef<TestConnectionSnackbarComponent>) => {
                                    if (snackBarRef && ApplicationSettingsService.isTestConnectionForDebugEnabled(dataSourceConfig)) {
                                        snackBarRef.onAction().pipe(take(1))
                                            .subscribe(() => {
                                                onTestConnectionForDebug$.next(data);
                                            });
                                    }
                                }
                            });

                            const result = <Action[]>[];
                            result.push(<Action>{
                                type: ApplicationSettingsActions.ASSIGN_DATA_SOURCE_CONFIG,
                                payload: {
                                    isValidConfiguration: isValidConfiguration,
                                    notValidConfigurationMessage: notValidConfigurationMessage
                                }
                            });

                            if (updateDataSourceConfig
                                && (isValidConfiguration !== state.dataSourceConfig.isValidConfiguration)
                                && state.dataSourceConfig.objectId) {
                                result.push(<Action>{
                                    type: ApplicationSettingsActions.UPDATE_DATA_SOURCE_CONFIG,
                                    payload: {loadInterationOrgsMapping: false, publish: false}
                                });
                            }

                            if (actionToDispatch) {
                                result.push(actionToDispatch);
                            }

                            return result;
                        })
                    );
            })
        )
    );

    testETXLDAPConnection = createEffect(() =>
            this.actions$.pipe(
                ofType(ApplicationSettingsActions.TEST_ETX_LDAP_CONNECTION),
                map((action: ApplicationSettingsActions.TestConnection) => action.payload),
                withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                concatMap(([{
                    dataSourceConfig,
                    isTestingConnectionSubject$,
                    isTestingETXLDAPConnectionSubject$,
                    isValidConnection$,
                    isValidETXLDAPConnection$,
                    onTestConnectionForDebug$,
                    updateDataSourceConfig,
                    actionToDispatch
                }, state]: [
                    {
                        dataSourceConfig: LDAPConfig;
                        isTestingConnectionSubject$: BehaviorSubject<boolean>;
                        isTestingETXLDAPConnectionSubject$: BehaviorSubject<boolean>;
                        isValidConnection$: BehaviorSubject<boolean>;
                        isValidETXLDAPConnection$: BehaviorSubject<boolean>;
                        onTestConnectionForDebug$: BehaviorSubject<DataSourceConfig>;
                        updateDataSourceConfig: boolean;
                        actionToDispatch?: UpdateDataSourceConfig;
                    }, fromApplicationSettings.State]) => {

                    let data = Object.assign({}, dataSourceConfig);

                    if (state.application && state.application.modes && (state.application.modes.length > 0)) {
                        data.mode = state.application.modes[0];
                    }

                    data = <LDAPConfig>this.applicationSettingsService.modifyDataSourceConfigBeforeSave(
                        data, state.application, state.defaultApplications
                    );
                    return this.applicationSettingsService.testETXLDAPConnection(data)
                        .pipe(
                            map((response: HttpResponse<string>) => {

                                if ((<any>response?.body)?.errors) {
                                    const message = (<any>response?.body)?.errors?.objectErrors[0]?.message || MESSAGES.UNKNOWN_ERROR;
                                    throw new Error(message);
                                }

                                isTestingETXLDAPConnectionSubject$.next(false);
                                let isTestingConnectConnection: boolean;
                                let isValidConnectConnection: boolean;

                                isTestingConnectionSubject$.pipe(take(1)).subscribe((isTestingConnection: boolean) => {
                                    isTestingConnectConnection = isTestingConnection;
                                });

                                isValidConnection$.pipe(take(1)).subscribe((isValidConnectionValue: boolean) => {
                                    isValidConnectConnection = isValidConnectionValue;
                                });

                                const isValidETXLDAPConnection = (response.status === 200)
                                    && (!response.body
                                        || (response.body && (<any>response.body).status && ((<any>response.body).status === 200)));
                                isValidETXLDAPConnection$.next(isValidETXLDAPConnection);

                                if (!isTestingConnectConnection && isValidConnectConnection) {
                                    if (isValidETXLDAPConnection) {
                                        this.snackBarService.showSnackBarMessage('Connection is ok.', 3000);
                                    } else {
                                        this.snackBarService.showSnackBarMessage('Failed to establish connection to authentication server', 5000);
                                    }
                                }
                            }),
                            catchError(error => {
                                isTestingETXLDAPConnectionSubject$.next(false);
                                isValidETXLDAPConnection$.next(false);
                                const notValidConfigurationMessage = (<any>error?.body)?.errors?.objectErrors[0]?.message
                                    || 'Failed to establish connection to authentication server';

                                let isTestingConnectConnection: boolean;
                                let isValidConnectConnection: boolean;

                                isTestingConnectionSubject$.pipe(take(1)).subscribe((isTestingConnection: boolean) => {
                                    isTestingConnectConnection = isTestingConnection;
                                });

                                isValidConnection$.pipe(take(1)).subscribe((isValidConnectionValue: boolean) => {
                                    isValidConnectConnection = isValidConnectionValue;
                                });

                                if (!isTestingConnectConnection && isValidConnectConnection) {
                                    this.snackBarService.showSnackBarMessage(notValidConfigurationMessage, 5000);
                                }
                                return observableOf([]);
                            })
                        );
                })
            ),
        {dispatch: false}
    );

    saveIntegrationTask = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_INTEGRATION_TASK),
            map((action: ApplicationSettingsActions.SaveIntegrationTask) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([taskData, state]: [{ task: IntegrationTask; lastTask: boolean; isAppCreated: boolean }, fromApplicationSettings.State]) => {
                taskData = {...taskData};
                taskData.task.mappings = taskData.task.mappings.map((mapping: IntegrationTaskMap) => {
                    if (mapping.mappingId === null) {
                        delete mapping.mappingId;
                    }
                    return mapping;
                });
                return this.http.put<IntegrationTask>(
                    `${environment.datahubBaseURI}/api/v2/applications/integrations/${state.integration.integrationId}/tasks/${taskData.task.taskId}`,
                    taskData.task)
                    .pipe(
                        mergeMap((task: IntegrationTask) => {
                                const result = [
                                    <Action>{
                                        type: ApplicationSettingsActions.ASSIGN_INTEGRATION_TASK,
                                        payload: task
                                    }
                                ];

                                if (taskData.lastTask) {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.SET_IS_LOADING,
                                        payload: false
                                    });
                                    if (taskData.isAppCreated) {
                                        this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                    }
                                }
                                return result;
                            }
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    );
            })
        )
    );

    saveIntegrationTasks = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_INTEGRATION_TASKS),
            map((action: ApplicationSettingsActions.SaveIntegrationTasks) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.authenticatedUserService.authenticatedUser$
            ),
            mergeMap(([payload, state, user]: [
                {
                    tasks: IntegrationTask[];
                    isAppCreated: boolean;
                    publish$: BehaviorSubject<boolean>;
                    showMessage$: BehaviorSubject<boolean>;
                    initiatorComponent: string;
                    errorMessage: string;
                },
                fromApplicationSettings.State,
                User
            ]) => {
                const integration = <Integration>{...state.integration};
                const saveIntegrationTasks$ = [];
                let integrationTasks: IntegrationTask[] = copyData(payload.tasks) || integration.tasks;

                // combine (unsplit) attributes
                const isDefaultIntegration = integration.state === IntegrationState.DEFAULT;
                const isOpenApplyOrManageBac = (state.dataSourceConfig.dataSourceType === DataSourceType.OpenApply)
                    || (state.dataSourceConfig.dataSourceType === DataSourceType.ManageBac);
                if (integration.mode === IntegrationMode.SYNC && (isOpenApplyOrManageBac || isDefaultIntegration)) {
                    integrationTasks = ApplicationSettingsService.combineIntegrationTaskMapAttributes(integrationTasks);
                }

                integrationTasks.forEach((task: IntegrationTask) => {
                    if (task.mappings) {
                        if (task.restrictedMappings && (task.restrictedMappings.length > 0)) {
                            task.mappings = task.mappings.concat((task.restrictedMappings));
                        }

                        task.mappings = task.mappings.map((mapping: IntegrationTaskMap) => {
                            if (mapping.mappingId === null) {
                                delete mapping.mappingId;
                            }
                            return mapping;
                        });
                    }
                    saveIntegrationTasks$.push(this.applicationSettingsService.saveIntegrationTask(integration.integrationId, task));
                });

                return combineLatest(saveIntegrationTasks$)
                    .pipe(
                        mergeMap((tasks: IntegrationTask[]) => {
                                tasks.forEach((task: IntegrationTask | HttpErrorResponse) => {
                                    if (task instanceof HttpErrorResponse) {
                                        throw <HttpErrorResponse>task;
                                    }
                                });

                                const result = [
                                    <Action>{
                                        type: ApplicationSettingsActions.ASSIGN_INTEGRATION_TASKS,
                                        payload: tasks
                                    }
                                ];

                                if (payload.isAppCreated) {

                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.SET_IS_LOADING,
                                        payload: false
                                    });

                                    if (payload.showMessage$) {
                                        payload.showMessage$
                                            .pipe(take(1))
                                            .subscribe((showMessage: boolean) => {
                                                if (showMessage) {
                                                    this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                                } else {
                                                    payload.showMessage$.next(true);
                                                }
                                            });
                                    } else {
                                        this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                    }

                                } else {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.SET_IS_LOADING,
                                        payload: false
                                    });
                                }

                                if (payload.publish$) {
                                payload.publish$
                                    .pipe(take(1))
                                    .subscribe((publish: boolean) => {
                                        if (publish) {
                                            result.push(<Action>{
                                                type: ApplicationSettingsActions.PUBLISH
                                            });
                                        } else {
                                            payload.publish$.next(true);
                                        }
                                    });
                            }

                            return result;
                        }
                        ),
                        catchError(error => {

                            let message: string;
                            if (error.status === 406) {
                                message = error.error;
                            } else {

                                if (error.status === 423 && this.authenticatedUserService.isSysAdmin(user)) {
                                    message = this.SAVE_EDIT_INTEGRATION_ERROR_MESSAGE;
                                } else {
                                    message = payload.errorMessage;
                                }
                            }
                            this.snackBarService.showSnackBarMessage(message, 3000);

                            return observableOf(
                                {
                                    type: ApplicationSettingsActions.ASSIGN_INTEGRATION_TASKS,
                                    payload: state.integration.tasks
                                },
                                {
                                    type: 'FAIL', payload: {error: error}
                                }
                            );
                        })
                    );
            })
        )
    );

    regenerateApplicationPIIShieldLevel = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.REGENERATE_APPLICATION_PII_SHIELD_LEVEL),
            map((action: ApplicationSettingsActions.RegenerateApplicationPIIShieldLevel) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([{integrationId, applicationGuid}, state]: [{integrationId: string; applicationGuid: string}, fromApplicationSettings.State]) =>
                this.piiShieldService.regenerateApplicationPIIShieldLevel(integrationId)
                    .pipe(
                        withLatestFrom(this.store.pipe(select(getAuthenticatedUserApplications))),
                        mergeMap(([updatedPIIShieldLevel, vendorApplications]: [PII_LEVEL, Application[]]) => {

                                const result = [<Action>{
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }];

                                let needToUpdateVendorApps: boolean;
                                const updatedVendorApps: Application[] = vendorApplications.map((app: Application) => {
                                    if ((app.guid === applicationGuid) && (app.piiLevel !== updatedPIIShieldLevel)) {
                                        app.piiLevel = updatedPIIShieldLevel;
                                        needToUpdateVendorApps = true;
                                    }
                                    return app;
                                });

                                if (needToUpdateVendorApps) {
                                    result.push(<Action>{
                                        type: AuthActionTypes.UPDATE_USER_APPS,
                                        payload: {userApplications: updatedVendorApps}
                                    });
                                }


                                return result;
                            }
                        ),
                        catchError(error => {
                            this.snackBarService.showSnackBarMessage(this.GET_APPLICATION_DATA_ERROR_MESSAGE, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    )
            )
        )
    );

    saveDictionaryByName = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_DICTIONARY_BY_NAME),
            map((action: ApplicationSettingsActions.SaveDictionaryByName) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([payload, state]: [{
                isAppCreated: boolean;
                publish$: BehaviorSubject<boolean> | null;
                dictionaryName: string;
                stepTitle: string;
                saveResponseToStore?: boolean;
            }, fromApplicationSettings.State]) => {
                const integration = <Integration>{...state.integration};
                let dictionaryEntries: DictionaryEntryModel[] = <DictionaryEntryModel[]>[];

                let dictionary;
                if (integration.dictionaries) {
                    dictionary = copyData(
                        integration.dictionaries.find((dict: DictionaryModel) =>
                            dict && dict.dictionaryName
                        && (dict.dictionaryName.toLocaleLowerCase() === payload.dictionaryName.toLocaleLowerCase())
                        )
                    );
                    dictionaryEntries = dictionary ? dictionary.entries : [];
                }

                return this.applicationSettingsService.saveDictionaryByName(
                    state.integration.integrationId, dictionaryEntries, payload.dictionaryName)
                    .pipe(
                        mergeMap((entries: DictionaryEntryModel[]) => {

                                if (dictionary) {
                                    dictionary.entries = entries;
                                }
                                if (payload.isAppCreated) {
                                    this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                }

                                const result = [
                                    <Action>{
                                        type: ApplicationSettingsActions.ASSIGN_DICTIONARY_BY_NAME,
                                        payload: {dictionaryName: payload.dictionaryName, dictionary: dictionary}
                                    },
                                    <Action>{
                                        type: ApplicationSettingsActions.SET_IS_LOADING,
                                        payload: false
                                    }
                                ];

                                if (payload.publish$) {
                                payload.publish$
                                    .pipe(take(1))
                                    .subscribe((publish: boolean) => {
                                        if (publish) {
                                            result.push(<Action>{
                                                type: ApplicationSettingsActions.PUBLISH
                                            });
                                        } else {
                                            payload.publish$.next(true);
                                        }
                                    });
                            }

                            return result;
                        }
                        ),
                        catchError(error => {
                            if (payload.stepTitle) {
                                this.snackBarService.showSnackBarMessage(
                                    `Unknown error occurred while saving ${payload.stepTitle} data.`,
                                    3000
                                );
                            }

                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    getDictionaryByName = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_DICTIONARY_BY_NAME),
            map((action: ApplicationSettingsActions.GetDictionaryByName) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([payload, state]: [{ providerGuid: string; dictionaryName: string; stepTitle: string }, fromApplicationSettings.State]) => this.applicationSettingsService.getDictionary(payload.providerGuid, state.dataSourceConfig.dname, payload.dictionaryName)
                .pipe(
                    mergeMap((dictionary: DictionaryModel) => [
                            {
                                type: ApplicationSettingsActions.ASSIGN_DICTIONARY_BY_NAME,
                                payload: {dictionaryName: payload.dictionaryName, dictionary: dictionary}
                            },
                        {
                            type: ApplicationSettingsActions.SET_IS_LOADING,
                            payload: false
                        }
                        ]
                    ),
                    catchError(error => {
                        this.snackBarService.showSnackBarMessage(`Unknown error occurred while saving ${payload.stepTitle} data.`, 3000);
                        return observableOf({type: 'FAIL', payload: {error: error}});
                    })
                ))
        )
    );

    saveQwiklabsDictionary = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_QWIKLABS_DICTIONARY),
            map((action: ApplicationSettingsActions.SaveQwiklabsDictionary) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([payload, state]: [{
                isAppCreated: boolean;
                dictionaryName: string;
                stepTitle: string;
                providerGuid?: string;
            }, fromApplicationSettings.State]) => {
                const integration = <Integration>{...state.integration};
                let dictionaryEntries: QwiklabsDictionaryEntryModel[] = <QwiklabsDictionaryEntryModel[]>[];

                let dictionary;
                if (integration.dictionaries) {
                    dictionary = copyData(
                        integration.dictionaries.find((dict: DictionaryModel) =>
                            dict.dictionaryName
                        && (dict.dictionaryName.toLocaleLowerCase() === payload.dictionaryName.toLocaleLowerCase())
                        )
                    );
                    dictionaryEntries = dictionary ? dictionary.entries : [];
                }

                return this.applicationSettingsService.saveQwiklabsDictionary(
                    payload.dictionaryName, state.dataSourceConfig.dname, dictionaryEntries
                )
                    .pipe(
                        mergeMap((qwikLabsDictionary: DictionaryModel) => {
                            if (payload.isAppCreated) {
                                this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                            }

                                const result = [<Action>{
                                    type: ApplicationSettingsActions.ASSIGN_DICTIONARY_BY_NAME,
                                    payload: {dictionaryName: payload.dictionaryName, dictionary: qwikLabsDictionary}
                                }];

                            if (payload.dictionaryName === QWIKLABS_ACTIVATED_COURSES_DICTIONARY_NAME) {
                                result.push(<Action>{
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                });
                            }

                            if (payload.dictionaryName === QWIKLABS_COURSE_MAPPING_DICTIONARY_NAME) {
                                result.push(<Action>{
                                    type: ApplicationSettingsActions.GET_DICTIONARY_BY_NAME,
                                    payload: {
                                        providerGuid: payload.providerGuid,
                                        dictionaryName: QWIKLABS_ACTIVATED_COURSES_DICTIONARY_NAME,
                                        stepTitle: payload.stepTitle
                                    }
                                });
                            }

                                return result;
                            }
                        ),
                        catchError(error => {
                            this.snackBarService.showSnackBarMessage(`Unknown error occurred while saving ${payload.stepTitle} data.`, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    resetGradeMapping = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.RESET_GRADE_MAPPING),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([action, state]: [any, fromApplicationSettings.State]) => {
                const dsConfigPath = DataSourcePathMap.get(state.dataSourceConfig.dataSourceType);
                return this.applicationSettingsService.resetGradeMapping(dsConfigPath, state.dataSourceConfig.dname)
                    .pipe(
                        mergeMap((gradeDictionary: DictionaryModel) => [
                                {
                                    type: ApplicationSettingsActions.ASSIGN_DICTIONARY_BY_NAME,
                                    payload: {dictionaryName: GRADE_DICTIONARY_NAME, dictionary: gradeDictionary}
                            },
                            {
                                type: ApplicationSettingsActions.SET_IS_LOADING,
                                payload: false
                            }
                            ]
                        ),
                        catchError(error => {
                            const message = this.RESET_GRADE_MAPPING_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    getQwiklabsCourses = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_QWIKLABS_COURSES),
            map((action: ApplicationSettingsActions.GetQwiklabsCourses) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([deploymentIds, state]: [string, fromApplicationSettings.State]) =>
                this.applicationSettingsService.getQwiklabsCourses(state.integration.source.dname, deploymentIds)
                    .pipe(
                        mergeMap((qwikLabsCourses: QwiklabsCourse[]) => [
                                {
                                    type: ApplicationSettingsActions.SET_QWIKLABS_COURSES,
                                    payload: qwikLabsCourses
                                },
                                {
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }
                            ]
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    ))
        )
    );

    evictQwiklabsCourses = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.EVICT_QWIKLABS_COURSES),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([payload, state]: [undefined, fromApplicationSettings.State]) => {
                const deploymentIds = state.integration.organizationsMappingModel.sourceOrganizations
                    .map((orgItem: OrgItem) => orgItem.identifier)
                    .join(',');
                return this.applicationSettingsService.evictQwiklabsCourses(state.dataSourceConfig.dname, deploymentIds)
                    .pipe(
                        mergeMap((response: any) => [
                                {
                                type: ApplicationSettingsActions.GET_QWIKLABS_COURSES,
                                payload: deploymentIds
                                },
                                {
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }
                            ]
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    );
            })
        )
    );

    saveSSOApplications = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_SSO_APPLICATIONS_DICTIONARY),
            map((action: ApplicationSettingsActions.SaveSSOApplicationsDictionary) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([payload, state]: [{ isAppCreated: boolean; stepTitle: string }, fromApplicationSettings.State]) => {
                const integration = <Integration>{...state.integration};
                let dictionaryEntries: DictionaryEntryModel[] = <DictionaryEntryModel[]>[];

                let dictionary;
                if (integration.dictionaries) {
                    dictionary = copyData(
                        integration.dictionaries.find((dict: DictionaryModel) =>
                            dict.dictionaryName
                        && (dict.dictionaryName.toLocaleLowerCase() === SSO_APPLICATIONS_DICTIONARY_NAME.toLocaleLowerCase())
                        )
                    );
                    dictionaryEntries = dictionary ? dictionary.entries : [];
                }

                return this.applicationSettingsService.saveAppLinkDictionaries(state.dataSourceConfig.dname, dictionaryEntries)
                    .pipe(
                        mergeMap((savedDictionary: DictionaryModel) => {
                            if (payload.isAppCreated) {
                                this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                            }

                                const result = [<Action>{
                                    type: ApplicationSettingsActions.ASSIGN_DICTIONARY_BY_NAME,
                                    payload: {dictionaryName: SSO_APPLICATIONS_DICTIONARY_NAME, dictionary: savedDictionary}
                                }];

                                result.push(<Action>{
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                });

                            return result;
                        }
                        ),
                        catchError(error => {
                            this.snackBarService.showSnackBarMessage(
                                `Unknown error occurred while saving ${payload.stepTitle} data.`,
                                3000
                            );
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    getSSOApplicationsDictionary = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_SSO_APPLICATIONS_DICTIONARY),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]) => this.applicationSettingsService.getAppLinkDictionaries(state.integration.source.dname)
                .pipe(
                    mergeMap((dictionary: DictionaryModel) => [{
                            type: ApplicationSettingsActions.ASSIGN_DICTIONARY_BY_NAME,
                            payload: {dictionaryName: SSO_APPLICATIONS_DICTIONARY_NAME, dictionary: dictionary}
                        }]
                    ),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    deleteIntegrationTasks = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.DELETE_INTEGRATION_TASKS),
            map((action: ApplicationSettingsActions.DeleteIntegrationTasks) => action.payload),
            mergeMap((deletedIntegrationTasks: IntegrationTask[]) => {
                const deleteIntegrationTasks$ = [];

                deletedIntegrationTasks.forEach((task: IntegrationTask) => {
                    deleteIntegrationTasks$.push(this.applicationSettingsService.deleteIntegrationTask(task.integrationId, task.taskId));
                });

                return combineLatest(deleteIntegrationTasks$)
                    .pipe(
                        mergeMap((response: any) => [{
                            type: ApplicationSettingsActions.INTEGRATION_TASKS_DELETED,
                                payload: deletedIntegrationTasks
                            },
                                {
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }]
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    );
            })
        )
    );

    changeCSVFileNamesOrAddIntegrationTasks = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.CHANGE_CSV_FILE_NAMES_OR_ADD_INTEGRATION_TASKS),
            map((action: ApplicationSettingsActions.ChangeCSVFileNamesOrAddIntegrationTasks) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.authenticatedUserService.authenticatedUser$
            ),
            mergeMap(([payload, state, user]: [{ integration: Integration; isAppCreated: boolean; publish: boolean }, fromApplicationSettings.State, User]
            ) => {
                const integration: Integration = payload.integration || {...state.integration};
                const changeCSVFileNameOfIntegrationTasksOrSaveIntegrationTasks$ = [];
                const updateSourceTypeInIntegrationTasks$ = [];

                integration.tasks.forEach((task: IntegrationTask, taskIndex) => {
                    if (task.taskId) {
                        const oldTask = state.integration.tasks.find((oldIntTask: IntegrationTask) => oldIntTask.taskId === task.taskId);
                        if (oldTask && (oldTask.csvFileName !== task.csvFileName)) {
                            changeCSVFileNameOfIntegrationTasksOrSaveIntegrationTasks$.push(
                                this.applicationSettingsService.changeCSVFileNameOfIntegrationTask(
                                    integration.integrationId,
                                    task.taskId,
                                    task.csvFileName
                                )
                            );
                        }
                    } else {
                        changeCSVFileNameOfIntegrationTasksOrSaveIntegrationTasks$.push(
                            this.applicationSettingsService.saveIntegrationTask(integration.integrationId, task)
                        );
                    }
                });

                if (changeCSVFileNameOfIntegrationTasksOrSaveIntegrationTasks$.length === 0) {
                    changeCSVFileNameOfIntegrationTasksOrSaveIntegrationTasks$.push(observableOf([]));
                }

                return combineLatest(changeCSVFileNameOfIntegrationTasksOrSaveIntegrationTasks$)
                    .pipe(
                        mergeMap((savedTasks: IntegrationTask[]) => {

                            savedTasks.forEach((task: IntegrationTask) => {
                                if (task?.taskId) {
                                    updateSourceTypeInIntegrationTasks$.push(
                                        this.applicationSettingsService.saveIntegrationTask(integration.integrationId, task)
                                    );
                                }
                            });

                            if (updateSourceTypeInIntegrationTasks$.length === 0) {
                                updateSourceTypeInIntegrationTasks$.push(observableOf([]));
                            }

                            return combineLatest(updateSourceTypeInIntegrationTasks$)
                                .pipe(
                                    mergeMap((tasks: IntegrationTask[]) => {

                                        const newTasks = tasks.filter((task: IntegrationTask) => task.taskId);
                                        if (payload.isAppCreated
                                            && newTasks === tasks) {
                                            this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                        }

                                        const result = [
                                            <Action>{
                                                type: ApplicationSettingsActions.ASSIGN_INTEGRATION_TASKS,
                                                payload: newTasks
                                            },
                                            <Action>{
                                                type: ApplicationSettingsActions.SET_IS_LOADING,
                                                payload: false
                                            }
                                        ];

                                        if (payload.publish) {
                                            result.push(<Action>{
                                                type: ApplicationSettingsActions.PUBLISH
                                            });
                                        }

                                        return result;
                                    }),
                                    catchError(error => {
                                        if (error.status === 423 && this.authenticatedUserService.isSysAdmin(user)) {
                                            this.snackBarService.showSnackBarMessage(this.SAVE_EDIT_INTEGRATION_ERROR_MESSAGE, 3000);
                                        } else {
                                            if (error.status === 406) {
                                                this.snackBarService.showSnackBarMessage(error.error, 3000);
                                            } else {
                                                this.snackBarService.showSnackBarMessage(this.SAVE_INTEGRATION_ENTITY_MAPPING_ERROR_MESSAGE, 3000);
                                            }
                                        }

                                        return observableOf(
                                            {
                                                type: 'FAIL',
                                                payload: {error: error}
                                            });
                                    })
                                );
                        }
                        ),
                        catchError(error => {
                            if (error.status === 423 && this.authenticatedUserService.isSysAdmin(user)) {
                                this.snackBarService.showSnackBarMessage(this.SAVE_EDIT_INTEGRATION_ERROR_MESSAGE, 3000);
                            } else {
                                if (error.status === 406) {
                                    this.snackBarService.showSnackBarMessage(error.error, 3000);
                                } else {
                                    this.snackBarService.showSnackBarMessage(this.SAVE_INTEGRATION_ENTITY_MAPPING_ERROR_MESSAGE, 3000);
                                }
                            }

                            return observableOf(
                                {
                                    type: 'FAIL',
                                    payload: {error: error}
                                });
                        })
                    );
            })
        )
    );

    getMergingRules = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_MERGING_RULES),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]) => this.applicationSettingsService.getMergingRules(state.integration.integrationId)
                .pipe(
                    mergeMap((mergingRulesData: DomainOfUniqueness[]) => [
                            {
                                type: ApplicationSettingsActions.SET_MERGING_RULES,
                                payload: mergingRulesData
                            }
                        ]
                    ),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    saveMergingRules = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_MERGING_RULES),
            map((action: ApplicationSettingsActions.SaveMergingRules) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([payload, state]:
                          [{ isAppCreated: boolean; saveResponseToStore?: boolean }, fromApplicationSettings.State]) => {
                const integration = <Integration>{...state.integration};
                let mergingRules = [];

                integration.tasks.forEach((task: IntegrationTask) => {
                    if (task.domainsOfUniqueness && task.domainsOfUniqueness.length > 0) {
                        mergingRules = mergingRules.concat(task.domainsOfUniqueness);
                    }
                });

                return this.applicationSettingsService.saveMergingRules(state.integration.integrationId, mergingRules)
                    .pipe(
                        mergeMap((mergingRulesData: DomainOfUniqueness[]) => {
                            if (payload.isAppCreated) {
                                this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                            }

                            const result: any[] = [{
                                type: ApplicationSettingsActions.SET_IS_LOADING,
                                payload: false
                            }];

                            if (payload.saveResponseToStore) {
                                result.push({
                                    type: ApplicationSettingsActions.SET_MERGING_RULES,
                                    payload: mergingRulesData
                                });
                            }

                                return result;
                            }
                        ),
                        catchError(error => {
                            const message = this.SAVE_MERGING_RULES_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    getMasterFields = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_MASTER_FIELDS),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            switchMap(([action, state]: [GetMasterFields, fromApplicationSettings.State]) => combineLatest([
                this.applicationSettingsService.getMasterFields(action.payload),
                this.store.pipe(select(getDefaultSchema)).pipe(
                    takeWhile(() => this.authenticatedUserService.isAuthenticated()),
                    distinctUntilChanged((a: AttributeMetadataModel[], b: AttributeMetadataModel[]) =>
                        JSON.stringify(a) === JSON.stringify(b)
                    ),
                    filter((defaultSchema: AttributeMetadataModel[]) => {
                        if (!defaultSchema) {
                            this.store.dispatch(new GetDefaultSchemaAction());
                        }
                        return !!defaultSchema;
                    }),
                    map((defaultSchema: AttributeMetadataModel[]) => {
                        let result: MasterFieldsMergingMetadataModel[] = [];
                        const dataSourceConfig = state.dataSourceConfig;
                        const dataSourceType = dataSourceConfig ? dataSourceConfig.dataSourceType : null;

                        defaultSchema
                            .filter((attr: AttributeMetadataModel) =>
                                ApplicationSettingsService.isTaskVisible(PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN.masterFields,
                                    attr.typeModel.denom, dataSourceType, dataSourceConfig))
                            .forEach((attr: AttributeMetadataModel) => {
                                result = result.concat(
                                    attr.propertyModel.filter((dataAttribute: DataAttribute) =>
                                        !!dataAttribute.filterable
                                    )
                                        .map((dataAttribute: DataAttribute) => new MasterFieldsMergingMetadataModel(
                                            attr.typeModel.denom,
                                            dataAttribute.friendlyName,
                                            dataAttribute.propertyName
                                        ))
                                );
                            });

                        return result;
                    })
                ),
                this.store.pipe(
                    select(getProviderApplications),
                    distinctUntilChanged((a: any, b: any) => JSON.stringify(a) == JSON.stringify(b)),
                    filter((applications: ApplicationProviderListModel[]) => !!applications),
                    map((applications: ApplicationProviderListModel[]) => {
                        const ownerId = this.authenticatedUserService.isSysAdmin()
                            ? action.payload
                            : this.authenticatedUserService.authenticatedUser.orgGuid;

                        return applications.filter(app => app.ownerId == ownerId && app.syncMode !== IntegrationMode.EXPORT);
                    })
                )
            ]).pipe(
                mergeMap((
                    [mergingRuleList, mergingMetadataModelList, providerApplications]: [MasterFieldsMergingRule[], MasterFieldsMergingMetadataModel[], ApplicationProviderListModel[]]
                ) => {
                    const masterFields: MasterFieldsModel = {
                        dataSourceModelList: copyData(providerApplications),
                        mergingRuleList: mergingRuleList,
                        mergingMetadataModelList: mergingMetadataModelList
                    };
                    return [
                        {
                            type: ApplicationSettingsActions.SET_MASTER_FIELDS,
                            payload: masterFields
                        }
                    ];
                }),
                catchError(error => {
                    const message = this.GET_MASTER_FIELDS_ERROR_MESSAGE;
                    this.snackBarService.showSnackBarMessage(message, 3000);
                    return observableOf(
                        {
                            type: ApplicationSettingsActions.SET_MASTER_FIELDS,
                            payload: new Error(error.message ? error.message : message)
                        }
                    );
                })
            ))
        )
    );

    saveAndDeleteMasterFields = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_AND_DELETE_MASTER_FIELDS),
            map((action: ApplicationSettingsActions.SaveAndDeleteMasterFields) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.authenticatedUserService.authenticatedUser$
            ),
            mergeMap((
                [{saveMergingRuleList = [], deleteMergingRuleList = [], mergingRulesWithoutChanges, savingMasterFieldsOptions},
                    state, user]: [{
                    saveMergingRuleList: MasterFieldsMergingRule[];
                    deleteMergingRuleList: string[];
                mergingRulesWithoutChanges: MasterFieldsMergingRule[];
                savingMasterFieldsOptions: {saveStep: boolean};
            }, fromApplicationSettings.State, User]) => {

                const saveMergingRules$ = saveMergingRuleList.length > 0
                    ? this.applicationSettingsService.saveMasterFields(saveMergingRuleList)
                    : observableOf(null);
                const deleteMergingRules$ = deleteMergingRuleList.length > 0
                    ? this.applicationSettingsService.deleteMasterFields(deleteMergingRuleList)
                    : observableOf(null);

                return combineLatest([deleteMergingRules$, saveMergingRules$])
                    .pipe(
                        mergeMap((savedMasterFields: MasterFieldsMergingRule[][]) => {
                            savedMasterFields = savedMasterFields.filter((masterFieldsMergingRules: MasterFieldsMergingRule[]) => !!masterFieldsMergingRules);

                            if (savingMasterFieldsOptions && savingMasterFieldsOptions.saveStep) {
                                this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                            }

                            return [{
                                type: ApplicationSettingsActions.SET_MASTER_FIELDS,
                                payload: <MasterFieldsModel>{
                                    dataSourceModelList: state.masterFields.dataSourceModelList,
                                    mergingMetadataModelList: state.masterFields.mergingMetadataModelList,
                                    mergingRuleList: mergingRulesWithoutChanges.concat([].concat.apply([], savedMasterFields))
                                }
                            }, {
                                type: ApplicationSettingsActions.SET_IS_LOADING,
                                payload: false
                            }];
                        }),
                        catchError(error => {
                            const message = error && error.status == '409' ?
                                this.MASTER_FIELD_ALREADY_EXIST_ERROR_MESSAGE :
                                this.SAVE_MASTER_FIELDS_ERROR_MESSAGE;

                            this.snackBarService.showSnackBarMessage(message, 5000);
                            return observableOf(
                                {
                                    type: ApplicationSettingsActions.GET_MASTER_FIELDS,
                                    payload: state.application.ownerId || user.orgGuid
                                },
                                {
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }
                            );
                        })
                    );
            })
        )
    );

    getAutoProvisioning = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_AUTO_PROVISIONING),
            map((action: ApplicationSettingsActions.GetAutoProvisioning) => action.payload),
            concatMap((appGuid: string) =>
                this.applicationSettingsService.getAutoProvisioning(appGuid)
                .pipe(
                    mergeMap((webhook: Webhook) => {
                        webhook.headers = webhook.headers
                            ? new Map(Object.entries(webhook.headers))
                            : webhook.headers;

                        return [{
                            type: ApplicationSettingsActions.SET_AUTO_PROVISIONING,
                            payload: webhook
                        }];
                    }),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    saveAutoProvisioning = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_AUTO_PROVISIONING),
            map((action: ApplicationSettingsActions.SaveAutoProvisioning) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([webhook, state]: [Webhook, fromApplicationSettings.State]) => {

                webhook.trialIntegrationGuids = state.webhook?.trialIntegrationGuids;

                return this.applicationSettingsService.saveAutoProvisioning(state.application.guid, webhook)
                    .pipe(
                        mergeMap((savedWebhook: Webhook) => {
                            return [
                                {
                                    type: ApplicationSettingsActions.SET_AUTO_PROVISIONING,
                                    payload: savedWebhook
                                },
                                {
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }
                            ];
                        }),
                        catchError(error => {
                            this.snackBarService.showSnackBarMessage(this.MAIN_SAVE_ERROR_MESSAGE, 5000);
                            return observableOf(
                                {
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }
                            );
                        })
                    );
            })
        )
    );

    initIntegrationState = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.INIT_INTEGRATION_STATE),
            map((action: ApplicationSettingsActions.InitIntegrationState) => action.payload),
            withLatestFrom(
                this.authenticatedUserService.authenticatedUser$,
                this.store.pipe(select(getApplicationSettings))
            ),
            concatMap(([isDataSchema, user, state]: [boolean, User, fromApplicationSettings.State]) => {
                let integrationState: IntegrationState;
                let integrationMode: IntegrationMode = state.integrationMode;
                const result = [];

                integrationState = ApplicationSettingsService.getIntegrationState(
                    this.authenticatedUserService.isVendor(user),
                    this.authenticatedUserService.isProvider(user),
                    this.authenticatedUserService.isSysAdmin(user),
                    isDataSchema
                );

                if (!state.integrationMode) {
                    integrationMode = ApplicationSettingsService.getIntegrationMode(
                        this.authenticatedUserService.isVendor(user),
                        this.authenticatedUserService.isProvider(user),
                        this.authenticatedUserService.isSysAdmin(user),
                        isDataSchema
                    );

                    result.push({
                        type: ApplicationSettingsActions.SET_INTEGRATION_MODE,
                        payload: integrationMode
                    });
                }

                result.push({
                    type: ApplicationSettingsActions.SET_INTEGRATION_STATE,
                    payload: integrationState
                });

                return result;
            })
        )
    );

    getIntegrationOrgMapping = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING, ApplicationSettingsActions.APPLICATION_CLEAR_STATE),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings))
            ),
            switchMap(([action, state]: [
                ApplicationSettingsActions.GetIntegrationOrgMapping | ApplicationSettingsActions.ApplicationClearState,
                fromApplicationSettings.State
            ]) => {

                let payload: { stopSpinner: boolean; showErrorMessage: boolean; messageAfterFinish: string };
                let integrationId: string;

                if (action.type === ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING) {
                    payload = action.payload;
                    integrationId = state.integration.integrationId;
                }

                return action.type === ApplicationSettingsActions.APPLICATION_CLEAR_STATE
                    ? observableOf()
                    : this.applicationSettingsService.getIntegrationOrgMapping(state.integration.integrationId)
                        .pipe(
                            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                            mergeMap(
                                ([orgsMapping, newState]: [OrganizationsMapping, fromApplicationSettings.State]) => {

                                    if (integrationId === newState?.integration?.integrationId) {

                                        if (orgsMapping?.edutoneOrganizations?.length > 0) {
                                            orgsMapping.edutoneOrganizations = orgsMapping.edutoneOrganizations
                                                .filter((org: OrgItem) => org?.identifier !== state.application.ownerId);
                                        }

                                        if (orgsMapping?.targetOrganizations?.length > 0) {
                                            orgsMapping.targetOrganizations = orgsMapping.targetOrganizations
                                                .filter((org: OrgItem) => org?.identifier !== state.application.ownerId);
                                        }

                                        if (orgsMapping?.sourceOrganizations?.length > 0) {
                                            orgsMapping.sourceOrganizations = orgsMapping.sourceOrganizations
                                                .filter((org: OrgItem) => org?.identifier !== state.application.ownerId);
                                        }

                                        const actions = <any[]>[{
                                            type: ApplicationSettingsActions.SET_INTEGRATION_ORG_MAPPING,
                                            payload: {organizationsMapping: orgsMapping}
                                        }];

                                        if ((state.integration.mode === IntegrationMode.IMPORT)
                                            && (state.integration?.source?.dataSourceType === DataSourceType.Qwiklabs)) {

                                            const deploymentIds = orgsMapping.sourceOrganizations
                                                .map((orgItem: OrgItem) => orgItem.identifier)
                                                .join(',');
                                            actions.push({
                                                type: ApplicationSettingsActions.GET_QWIKLABS_COURSES,
                                                payload: deploymentIds
                                            });
                                        } else {
                                            if (payload && payload.stopSpinner) {
                                                actions.push({
                                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                                    payload: false
                                                });
                                            }
                                        }

                                        if (payload.messageAfterFinish) {
                                            this.snackBarService.showSnackBarMessage(payload.messageAfterFinish, 5000);
                                        }

                                        return actions;
                                    }
                                }
                            ),
                            catchError(error => {
                                if (payload.showErrorMessage) {
                                    if (ApplicationSettingsService.isSupportMappingRulesDataSourceTypes(
                                        state.application, state.dataSourceConfig, state.integration)) {
                                        const message = `Unknown error occurred while loading OU tree from ${state.dataSourceConfig.dataSourceType} server.`;
                                        this.snackBarService.showSnackBarMessage(message, 5000);
                                    } else {
                                        const message = this.GET_INTEGRATION_ORGS_MAPPING_ERROR_MESSAGE;
                                        this.snackBarService.showSnackBarMessage(message, 5000);
                                    }
                                }

                                return observableOf(
                                    {
                                        type: ApplicationSettingsActions.SET_INTEGRATION_ORG_MAPPING,
                                        payload: {organizationsMapping: new OrganizationsMapping()}
                                    },
                                    {
                                        type: 'FAIL',
                                        payload: {error: error}
                                    }
                                );
                            })
                        );
            }),
        )
    );

    getIntegrationRules = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_INTEGRATION_RULES),
            map((action: ApplicationSettingsActions.GetIntegrationRules) => action),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]) => this.applicationSettingsService.getIntegrationRules(action.payload)
                .pipe(
                    map((rules: IntegrationRulesMap) => rules ? rules : {}),
                    tap((rules: IntegrationRulesMap) =>
                        state.integration.tasks.forEach((task: IntegrationTask) => {
                            if (!rules[task.taskId]) {
                                rules[task.taskId] = {
                                    filtrations: <RequestedFilterViewModel[]>[],
                                    sso: <RequestedFilterViewModel[]>[],
                                    transformations: <EntityFilterModel[]>[],
                                    streamTransformations: <EntityFilterModel[]>[],
                                    validations: <EntityFilterModel[]>[]
                                };
                            }
                        })
                    ),
                    mergeMap(
                        (rules: IntegrationRulesMap) => {
                            if (this.spinner && this.spinner.active) {
                                this.spinner.stop();
                            }

                            return [
                                {
                                    type: ApplicationSettingsActions.SET_INTEGRATION_RULES,
                                    payload: rules
                                },
                                {
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                }
                            ];
                        }
                    ),
                    catchError(error => {
                        if (this.spinner && this.spinner.active) {
                            this.spinner.stop();
                        }

                        // check to prevent appearance message on logout
                        if (state.application && state.application.guid
                            && state.integration && state.integration.tasks && (state.integration.tasks.length > 0)) {
                            const message = this.GET_INTEGRATION_RULES_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 5000);
                        }
                        return observableOf(
                            {
                                type: ApplicationSettingsActions.SET_INTEGRATION_RULES,
                                payload: []
                            },
                            {
                                type: 'FAIL',
                                payload: {error: error}
                            }
                        );
                    })
                ))
        )
    );

    saveIntegrationRules = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_INTEGRATION_RULES),
            map((action: ApplicationSettingsActions.SaveIntegrationRules) => action.payload),
            withLatestFrom(this.authenticatedUserService.authenticatedUser$),
            switchMap(([payload, user]: [{
                integrationId: string;
                param: string;
                rules: EntityFilterModel[];
                message: string;
                publish$: BehaviorSubject<boolean>;
                saveResponseToStore: boolean;
            showMessage$: BehaviorSubject<boolean>;
            stopSpinner$: BehaviorSubject<boolean>;
            institutionGuid: string;
        }, User]) => {
                let taskPropertyName = payload.param === STREAM ? STREAM_TRANSFORMATIONS : payload.param;

                if (payload.param === STREAM) {
                    taskPropertyName = STREAM_TRANSFORMATIONS;
                    payload.rules = (<MappingRule[]>payload.rules).map((mappingRule: MappingRule) => {
                        for (const [key, value] of mappingRule.customValues) {
                            mappingRule.customValues[key] = value;
                        }

                        return mappingRule;
                    });
                }

                if (payload.param === TRANSFORMATIONS) {
                    payload.param = TRANSFORMATIONS;
                    payload.rules = payload.rules.map((transformRule: EntityFilterModel) => {
                        for (const [key, value] of transformRule.customValues) {
                            transformRule.customValues[key] = value;
                            for (const [oldValue, newValue] of this.LOGIN_CREDENTIALS_ATTRIBUTES_MAP) {
                                value.name = (<string>value.name).split(oldValue).join(newValue);
                                value.id = (<string>value.id).split(oldValue).join(newValue);
                            }
                        }

                        return transformRule;
                    });
                }

                return this.applicationSettingsService.saveIntegrationRules(payload.integrationId, payload.param, payload.rules)
                    .pipe(
                        map((rules: EntityFilterModel[]) => {
                            const result = {};
                            rules.forEach((rule: EntityFilterModel) => {
                                if (!result[rule.taskId]) {
                                    result[rule.taskId] = {};
                                    result[rule.taskId][taskPropertyName] = [];
                                }
                                result[rule.taskId][taskPropertyName].push(rule);
                            });

                            if (payload.message) {
                                if (payload.showMessage$) {
                                    payload.showMessage$
                                        .pipe(take(1))
                                        .subscribe((showMessage: boolean) => {
                                            if (showMessage) {
                                                this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                            } else {
                                                payload.showMessage$.next(true);
                                            }
                                        });
                                } else {
                                    this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                }
                            }

                            return result;
                        }),
                        mergeMap(
                            (rules: any) => {
                                const result = [];
                                if (payload.saveResponseToStore) {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.SET_INTEGRATION_RULES,
                                        payload: rules
                                    });
                                }

                                if (payload.param === FILTRATIONS || payload.param === SSO_FILTRATIONS || payload.param === STREAM) {
                                    result.push(<Action>{
                                        type: ApplicationSettingsActions.SET_IS_LOADING,
                                        payload: false
                                    });
                                } else {
                                    if (payload.stopSpinner$) {
                                        payload.stopSpinner$
                                            .pipe(take(1))
                                            .subscribe((stopSpinner: boolean) => {
                                                if (stopSpinner) {
                                                    result.push(<Action>{
                                                        type: ApplicationSettingsActions.SET_IS_LOADING,
                                                        payload: false
                                                    });
                                                }
                                            });
                                    }
                                }

                                if (payload.publish$) {
                                    payload.publish$
                                        .pipe(take(1))
                                        .subscribe((publish: boolean) => {
                                            if (publish) {
                                                result.push(<Action>{
                                                    type: ApplicationSettingsActions.PUBLISH
                                                });
                                            } else {
                                                payload.publish$.next(true);
                                            }
                                        });
                                }

                                return result;
                            }
                        ),
                        catchError(error => {
                            let message: string;
                            if (error.status === 423 && this.authenticatedUserService.isSysAdmin(user)) {
                                message = this.SAVE_EDIT_INTEGRATION_ERROR_MESSAGE;
                            } else {
                                message = this.MAIN_SAVE_ERROR_MESSAGE;
                            }
                            this.snackBarService.showSnackBarMessage(message, 3000);

                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    getRosterAccessProgress = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_ROSTER_ACCESS_PROGRESS),
            map((action: ApplicationSettingsActions.GetRosterAccessProgress) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([districtId, state]: [string, fromApplicationSettings.State]) => {
                if (!state.integration.integrationId || !districtId) {
                    return observableOf({
                        type: ApplicationSettingsActions.SET_ROSTER_ACCESS_PROGRESS,
                        payload: new RosterAccessProgress(100)
                    });
                }

                return this.integrationService.getRosterAccessProgress(state.integration.integrationId, districtId)
                    .pipe(
                        mergeMap(
                            (rosterAccessProgress: number) => [{
                                type: ApplicationSettingsActions.SET_ROSTER_ACCESS_PROGRESS,
                                payload: new RosterAccessProgress(rosterAccessProgress)
                            }]
                        ),
                        catchError(error => observableOf({
                            type: ApplicationSettingsActions.SET_ROSTER_ACCESS_PROGRESS,
                            payload: new RosterAccessProgress(100)
                        }))
                    );
            })
        )
    );

    saveIntegrationOrgMapping = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_INTEGRATION_ORG_MAPPING),
            map((action: ApplicationSettingsActions.SaveIntegrationOrgMapping) => action.payload),
            withLatestFrom(
                this.store.pipe(select(getApplicationSettings)),
                this.authenticatedUserService.authenticatedUser$
            ),
            switchMap(([
                           {
                               showSaveMessage,
                               publish,
                    savingMappingRulesOptions
                },
                state,
                user]:
                       [{
                           showSaveMessage: boolean;
                           publish: boolean;
                           savingMappingRulesOptions?: {
                               publish$: BehaviorSubject<boolean>;
                               mappingRules: MappingRule[];
                               saveResponseToStore: boolean;
                           };
                       },
                           fromApplicationSettings.State,
                           User
                       ]) => {

                const isQwiklabsDataSource = state.integration.source.dataSourceType === DataSourceType.Qwiklabs;
                const newOrgIdentifier = isQwiklabsDataSource ? NEW_QWIKLABS_SCHOOL_IDENTIFIER : NEW_ORG_IDENTIFIER;
                const isSFTPExport = (state.integration.source.dataSourceType === DataSourceType.EduTone)
                    && (state.integration.target.dataSourceType === DataSourceType.SFTP);

                let saveKeyName = isQwiklabsDataSource ? 'source' : 'edutone';
                if (state.integration.source.dataSourceType === DataSourceType.Blackboard) {
                    const isMappingToNodes = state.orgsMapping.some((orgMapping: OrgMapping) => {
                        const sourceOrganization: OrgItem = state.integration.organizationsMappingModel.sourceOrganizations
                            .find((org: OrgItem) => org.identifier === orgMapping?.source?.identifier);
                        return sourceOrganization?.parentId;
                    });
                    const isMappingToRoot = !isMappingToNodes;
                    if (isMappingToRoot) {
                        saveKeyName = 'source';
                    }
                }

                const data: OrgMapping[] = copyData(state.orgsMapping)
                    .filter((orgMapping: OrgMapping) =>
                        ((orgMapping && orgMapping[saveKeyName] && !orgMapping.existing) && !isSFTPExport)
                        || ((orgMapping && orgMapping[saveKeyName] && !orgMapping.existing && orgMapping.enabled) && isSFTPExport)
                        // rows with not empty New GG4L School fields in Qwiklabs Orgs Mapping
                        || (isQwiklabsDataSource && (orgMapping.edutone.identifier === NEW_ORG_IDENTIFIER) && orgMapping.edutone.name && orgMapping[saveKeyName])
                    )
                    .map((orgMapping: OrgMapping) => {

                        if (orgMapping[saveKeyName].identifier === newOrgIdentifier) {
                            orgMapping.customOrgName = orgMapping[saveKeyName].name.trim();
                            if (state.integration.source.dataSourceType === DataSourceType.Qwiklabs) {
                                orgMapping[saveKeyName].identifier = `${orgMapping[saveKeyName].identifier}${genId()}`;
                                if (orgMapping.edutone.identifier === NEW_ORG_IDENTIFIER) {
                                    orgMapping.edutone = null;
                                }
                            } else {
                                orgMapping[saveKeyName] = null;
                            }
                        }

                        return orgMapping;
                    });

                return this.applicationSettingsService.saveIntegrationOrgMapping(state.integration.integrationId, data)
                    .pipe(
                        mergeMap(
                            (responseOrgsMapping: OrganizationsMapping) => {
                                const organizationsMappingModel = Object.assign(state.integration.organizationsMappingModel,
                                    {mappingList: <OrgMapping[]>responseOrgsMapping.mappingList});

                                // remove current District Provider org from edutoneOrganizations
                                organizationsMappingModel.edutoneOrganizations = organizationsMappingModel.edutoneOrganizations
                                    .filter((org: OrgItem) => org.identifier !== state.application.ownerId);

                                // add to edutoneOrganizations if there are some that were not created yet, but are already mapped
                                const edutoneOrgsObj = {};
                                organizationsMappingModel.edutoneOrganizations.forEach((edutoneOrg: OrgItem) => {
                                    edutoneOrgsObj[edutoneOrg.identifier] = edutoneOrg.name;
                                });
                                organizationsMappingModel.mappingList.forEach((orgMap: OrgMapping) => {
                                    if (!edutoneOrgsObj[orgMap.edutone.identifier]) {
                                        organizationsMappingModel.edutoneOrganizations.push(
                                            new OrgItem(orgMap.edutone.identifier, orgMap.edutone.name));
                                    }
                                });

                                const result = <any>[];
                                if (state.integration.source.dataSourceType === DataSourceType.Qwiklabs) {
                                    result.push({
                                        type: ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING,
                                        payload: {stopSpinner: false, showErrorMessage: false, messageAfterFinish: ''}
                                    });
                                } else {
                                    result.push({
                                        type: ApplicationSettingsActions.SET_INTEGRATION_ORG_MAPPING,
                                        payload: {organizationsMapping: organizationsMappingModel}
                                    });
                                }

                                // if we are saving Mapping rules
                                if (savingMappingRulesOptions && savingMappingRulesOptions.mappingRules) {
                                    savingMappingRulesOptions.mappingRules = savingMappingRulesOptions.mappingRules
                                        .map((mappingRule: MappingRule) => {

                                            const oldSchool = mappingRule.customValues.get(SCHOOL_ID);
                                            if (oldSchool && (oldSchool.id === NEW_ORG_IDENTIFIER)) {
                                                const orgMappingWithCreatedSchool = responseOrgsMapping.mappingList
                                                    .find((orgMapping: OrgMapping) => oldSchool.name === orgMapping.edutone.name);
                                                if (orgMappingWithCreatedSchool) {
                                                    const newSchool = orgMappingWithCreatedSchool.edutone;
                                                    mappingRule.customValues.set(SCHOOL_ID, new Argument(newSchool.identifier, newSchool.name));
                                                    mappingRule.schoolGuid = newSchool.identifier;
                                                }
                                            }
                                            return mappingRule;
                                        });

                                    result.push({
                                        type: ApplicationSettingsActions.SAVE_INTEGRATION_RULES,
                                        payload: {
                                            integrationId: state.integration.integrationId,
                                            param: STREAM,
                                            rules: savingMappingRulesOptions.mappingRules,
                                            message: state.application.configurationStep === APP_LAST_STEP ? MESSAGES.SAVED : '',
                                            publish$: savingMappingRulesOptions.publish$,
                                            saveResponseToStore: savingMappingRulesOptions.saveResponseToStore
                                        }
                                    });
                                }

                                if (publish) {
                                    result.push({
                                        type: ApplicationSettingsActions.PUBLISH
                                    });
                                }

                                if (showSaveMessage) {
                                    this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                                }

                                return result;
                            }
                        ),
                        catchError(error => {
                            let message = this.SAVE_INTEGRATION_ORGS_MAPPING_ERROR_MESSAGE;
                            // if we are saving Mapping rules
                            if (savingMappingRulesOptions && savingMappingRulesOptions.mappingRules) {
                                message = this.SAVE_MAPPING_RULES_ERROR_MESSAGE;
                            }

                            const result: any[] = [
                                {
                                    type: ApplicationSettingsActions.GET_INTEGRATION_ORG_MAPPING,
                                    payload: {stopSpinner: false, showErrorMessage: true, messageAfterFinish: ''}
                                },
                                {
                                    type: 'FAIL',
                                    payload: {error: error}
                                }
                            ];

                            if (state.integration.source.dataSourceType === DataSourceType.Qwiklabs) {
                                result.push({
                                    type: ApplicationSettingsActions.SET_IS_LOADING,
                                    payload: false
                                });
                            }

                            this.snackBarService.showSnackBarMessage(message, 3000);
                            return result;
                        })
                    );
            })
        )
    );

    deleteApplication = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.DELETE_APPLICATION),
            map((action: ApplicationSettingsActions.DeleteApplication) => action.payload),
            withLatestFrom(
                this.authenticatedUserService.authenticatedUser$,
                this.store.pipe(select(getAuthenticatedUserApplications)),
            ),
            concatMap(([appGuid, user, vendorApplications]: [string, User, Application[]]) => {
                if (this.authenticatedUserService.isVendor(user)) {
                    const otherApp: Application | undefined = vendorApplications.find((app: Application) => (app.guid !== appGuid) && (!app?.isDeleting));
                    const updatedVendorApplications = vendorApplications.map((app: Application) => {
                        app.isDeleting = app?.guid === appGuid;
                        return app;
                    });
                    this.store.dispatch(new UpdateUserApps({userApplications: updatedVendorApplications}));
                    this.store.dispatch(new UpdateVendorCurrentAppAction({currentAppId: otherApp ? otherApp.guid : ''}));
                }

                return this.http.delete(`${environment.datahubBaseURI}/api/v2/applications/${appGuid}`)
                    .pipe(
                        withLatestFrom(this.store.pipe(select(getDeletingApplications))),
                        switchMap(([res, deletingApplications]: [any, ApplicationProviderListModel[]]) => {
                            if (this.authenticatedUserService.isProvider(user)) {
                                return this.store.pipe(select(getProviderApplications),
                                    take(1),
                                    map((applications: ApplicationProviderListModel[]) => {

                                        let deletingApplication: ApplicationProviderListModel;
                                        const newProviderApplications = applications.map((app: ApplicationProviderListModel) => {
                                            if (app.guid === appGuid) {
                                                app.isDeleting = true;
                                                deletingApplication = Object.assign({}, app);
                                            }
                                            return app;
                                        });
                                        this.store.dispatch(new IntegrationActions.GetProviderApplicationsSuccessAction(
                                            {applications: newProviderApplications})
                                        );

                                        deletingApplications = (deletingApplications || []).concat([deletingApplication]);
                                        deletingApplications = deletingApplications.filter((app: ApplicationProviderListModel) => !!app);
                                        this.store.dispatch(new IntegrationActions.SetDeletingApplicationsAction(deletingApplications));

                                        return res;
                                    })
                                );
                            } else {
                                return observableOf(res);
                            }
                        }),
                        mergeMap((res) => {
                            this.searchService.removeFromRecentByGuid(appGuid);

                            return [(this.authenticatedUserService.isVendor(user)) ?
                                observableOf(res) :
                                new ApplicationSettingsActions.ApplicationClearState()
                            ];
                        }),
                        delay(this.authenticatedUserService.isVendor(user) ? 30000 : 0),
                        mergeMap((res) => [(this.authenticatedUserService.isVendor(user))
                            ? new LoadVendorApps({user: user, deletingAppGuid: appGuid})
                            : <any>res]),
                        catchError(error => {
                            const updatedVendorApplications = vendorApplications.map((app: Application) => {
                                app.isDeleting = (app?.guid === appGuid) ? false : app.isDeleting;
                                return app;
                            });
                            this.store.dispatch(new UpdateUserApps({userApplications: updatedVendorApplications}));
                            this.store.dispatch(new ApplicationSettingsActions.SetIsDeletingApplication(false));
                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    );
            })
        )
    );

    deleteDataSourceConfig = createEffect(() =>
            this.actions$.pipe(
                ofType(ApplicationSettingsActions.DELETE_DATA_SOURCE_CONFIG),
                withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                concatMap(([payload, state]) => this.applicationSettingsService.deleteDataSourceConfig(state.application.dataSourceType, state.integration.target.dname)
                    .pipe(
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    ))
            ),
        {dispatch: false}
    );

    deleteIntegrationOrgMapping = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.DELETE_INTEGRATION_ORG_MAPPING),
            map((action: ApplicationSettingsActions.DeleteIntegrationOrgMapping) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([data, state]
                          : [{ orgsMapping: OrgMapping[]; orgMapping: OrgMapping; blackboardViewOptionControl: FormControl },
                fromApplicationSettings.State]) => {
                const orgMapping = data.orgMapping;
                const orgsMapping = data.orgsMapping;
                const headers = new HttpHeaders({
                    'X-Auth-Token': this.tokenService.getToken(),
                    'Content-Type': 'application/json',
                });
                const req = new HttpRequest(
                    'DELETE',
                    `${environment.datahubBaseURI}/api/v2/applications/integrations/${state.integration.integrationId}/organizations/mapping`,
                    orgMapping,
                    {
                        responseType: 'text' as const,
                        headers: headers
                    });
                return this.http.request(req)
                    .pipe(
                        tap(() => observableOf()),
                        mergeMap(
                            (response: HttpResponse<any>) => {
                                if (response.status >= 200 && response.status < 300) {
                                    return [
                                        {
                                            type: ApplicationSettingsActions.INTEGRATION_ORG_MAPPING_DELETED,
                                            payload: {
                                                orgsMapping: orgsMapping,
                                                orgMapping: orgMapping,
                                                blackboardViewOptionControl: data.blackboardViewOptionControl
                                            }
                                        }
                                    ];
                                }
                                return observableOf();
                            }
                        ),
                        catchError(error => {
                            orgMapping.isDeleting = false;
                            const message = this.DELETE_INTEGRATION_ORG_MAPPING_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 5000);

                            return observableOf(
                                {
                                    type: ApplicationSettingsActions.ASSIGN_INTEGRATION_ORGS_MAPPING,
                                    payload: orgsMapping
                                },
                                {
                                    type: 'FAIL',
                                    payload: {error: error}
                                }
                            );
                        })
                    );
            })
        )
    );

    deleteIntegrationOrgMappingList = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.DELETE_INTEGRATION_ORG_MAPPING_LIST),
            map((action: ApplicationSettingsActions.DeleteIntegrationOrgMappingList) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([{orgsMappingDeleteList, blackboardViewOptionControl}, state]
                          : [{ orgsMappingDeleteList: OrgMapping[]; blackboardViewOptionControl: FormControl }, fromApplicationSettings.State]) => {
                const headers = new HttpHeaders({
                    'X-Auth-Token': this.tokenService.getToken(),
                    'Content-Type': 'application/json',
                });
                const req = new HttpRequest(
                    'DELETE',
                    `${environment.datahubBaseURI}/api/v2/applications/integrations/${state.integration.integrationId}/organizations/mapping/bulk`,
                    orgsMappingDeleteList,
                    {
                        responseType: 'text' as const,
                        headers: headers
                    });
                return this.http.request(req)
                    .pipe(
                        tap(() => observableOf()),
                        mergeMap(
                            (response: HttpResponse<any>) => {
                                if (response
                                    && response.status >= 200
                                    && response.status < 300) {

                                    const organizationsMapping: OrganizationsMapping = {...state.integration.organizationsMappingModel};
                                    organizationsMapping.mappingList = [];

                                    return observableOf(
                                        {
                                            type: ApplicationSettingsActions.SET_INTEGRATION_ORG_MAPPING,
                                            payload: {
                                                organizationsMapping: organizationsMapping,
                                                blackboardViewOptionControl: blackboardViewOptionControl
                                            }
                                        }
                                    );
                                }
                                return observableOf();
                            }
                        ),
                        catchError(error => {
                            const message = this.DELETE_INTEGRATION_ORG_MAPPING_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 5000);

                            const oldBlackboardViewOptionValue = blackboardViewOptionControl.value === BLACKBOARD_ROOT
                                ? BLACKBOARD_NODES
                                : BLACKBOARD_ROOT;
                            blackboardViewOptionControl.setValue(oldBlackboardViewOptionValue);

                            return observableOf(
                                {
                                    type: ApplicationSettingsActions.SET_INTEGRATION_ORG_MAPPING,
                                    payload: {
                                        organizationsMapping: state.integration.organizationsMappingModel,
                                        blackboardViewOptionControl: blackboardViewOptionControl
                                    }
                                },
                                {
                                    type: 'FAIL',
                                    payload: {error: error}
                                }
                            );
                        })
                    );
            })
        )
    );

    deleteIntegrationOrgsMappingOnMappingRulesSaving = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.DELETE_INTEGRATION_ORGS_MAPPING_ON_MAPPING_RULES_SAVING),
            map((action: ApplicationSettingsActions.DeleteIntegrationOrgsMappingOnMappingRulesSaving) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([{orgsMappingDeleteList, orgsMappingSaveList, savingMappingRulesOptions}, state]: [{
                orgsMappingDeleteList: OrgMapping[];
                orgsMappingSaveList: OrgMapping[];
                savingMappingRulesOptions: {
                    publish$: BehaviorSubject<boolean>;
                    mappingRules: MappingRule[];
                    saveResponseToStore: boolean;
                };
            },
            fromApplicationSettings.State
        ]) => {
                const headers = new HttpHeaders({
                    'X-Auth-Token': this.tokenService.getToken(),
                    'Content-Type': 'application/json',
                });
                const req = new HttpRequest(
                    'DELETE',
                    `${environment.datahubBaseURI}/api/v2/applications/integrations/${state.integration.integrationId}/organizations/mapping/bulk`,
                    orgsMappingDeleteList,
                    {
                        responseType: 'text' as const,
                        headers: headers
                    });
                return this.http.request(req)
                    .pipe(
                        tap(() => observableOf()),
                        mergeMap(
                            (response: HttpResponse<any>) => {
                                if (response
                                && response.status >= 200
                                && response.status < 300) {

                                    const newIntegration: Integration = <Integration>{...state.integration};
                                    let orgsMapping: OrgMapping[] = <OrgMapping[]>[...state.orgsMapping];
                                    const orgsMappingDeleteObj = {};
                                    const sourceOrgsObj = {};
                                    const orgsToBeDeletedObj = {}; // orgs was only in mapping, but not in source orgs and now was deleted - delete orgMapping
                                    const orgsMappingSaveObj = {};
                                    const otherDS: string = (state.integration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
                                    const result = [];

                                    orgsMappingDeleteList.forEach((orgMapping: OrgMapping) => {
                                        orgsMappingDeleteObj[orgMapping[otherDS].identifier] = orgMapping[otherDS].identifier;
                                    });
                                    orgsMapping = orgsMapping.map((orgMapping: OrgMapping) => {
                                        if (orgsMappingDeleteObj[orgMapping[otherDS].identifier]) {
                                            orgMapping.edutone = null;
                                            orgMapping.createNewOrg = false;
                                            orgMapping.existing = false;
                                            orgMapping.customOrgName = null;
                                        }
                                        return orgMapping;
                                    });
                                    newIntegration.organizationsMappingModel.mappingList = newIntegration.organizationsMappingModel.mappingList
                                        .filter((orgMapping: OrgMapping) => !orgsMappingDeleteObj[orgMapping[otherDS].identifier]);

                                    state.integration.organizationsMappingModel[`${otherDS}Organizations`].forEach((org: OrgItem) => {
                                        sourceOrgsObj[org.identifier] = org.identifier;
                                    });
                                    orgsMappingDeleteList.forEach((orgMapping: OrgMapping) => {
                                    // orgs was only in mapping, but not in source orgs and now was deleted - delete orgMapping
                                        if (!sourceOrgsObj[orgMapping[otherDS].identifier]) {
                                            orgsToBeDeletedObj[orgMapping[otherDS].identifier] = orgMapping[otherDS].identifier;
                                        }
                                    });
                                    orgsMapping = orgsMapping.filter((orgMapping: OrgMapping) => !orgsToBeDeletedObj[orgMapping[otherDS].identifier]);

                                    orgsMappingSaveList.forEach((orgMapping: OrgMapping) => {
                                        orgsMappingSaveObj[orgMapping[otherDS].identifier] = orgMapping;
                                    });
                                    orgsMapping = orgsMapping.map((orgMapping: OrgMapping) => {
                                        const changedOrgMapping = (<OrgMapping>orgsMappingSaveObj[orgMapping[otherDS].identifier]);
                                        if (changedOrgMapping
                                        && (orgMapping[otherDS].identifier === changedOrgMapping[otherDS].identifier)) {
                                            orgMapping.edutone = changedOrgMapping.edutone;
                                            orgMapping.createNewOrg = changedOrgMapping.createNewOrg;
                                        }
                                        return orgMapping;
                                    });

                                    result.push(
                                        <Action>{
                                            type: ApplicationSettingsActions.ASSIGN_INTEGRATION_ORGS_MAPPING,
                                            payload: orgsMapping
                                        },
                                        <Action>{
                                            type: ApplicationSettingsActions.ASSIGN_INTEGRATION,
                                            payload: newIntegration
                                        }
                                    );

                                    if (orgsMappingSaveList.length > 0) {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.SAVE_INTEGRATION_ORG_MAPPING,
                                            payload: {
                                                showSaveMessage: false,
                                                publish: false,
                                                savingMappingRulesOptions: savingMappingRulesOptions
                                            }
                                        });
                                    } else {
                                        result.push(<Action>{
                                            type: ApplicationSettingsActions.SAVE_INTEGRATION_RULES,
                                            payload: {
                                                integrationId: state.integration.integrationId,
                                                param: STREAM,
                                                rules: savingMappingRulesOptions.mappingRules,
                                                message: state.application.configurationStep === APP_LAST_STEP ? MESSAGES.SAVED : '',
                                                publish$: savingMappingRulesOptions.publish$,
                                                saveResponseToStore: savingMappingRulesOptions.saveResponseToStore
                                            }
                                        });
                                    }

                                    return result;
                                }
                                return observableOf();
                            }
                        ),
                        catchError(error => {
                            const message = this.SAVE_MAPPING_RULES_ERROR_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 5000);

                            return observableOf({
                                type: 'FAIL',
                                payload: {error: error}
                            });
                        })
                    );
            })
        )
    );

    saveIntegrationProcessConfig = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.SAVE_INTEGRATION_PROCESS_CONFIG),
            map((action: ApplicationSettingsActions.SaveIntegrationProcessConfig) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([payload, state]: [{ processConfigModel?: ProcessConfig[]; expirationTs?: Date; isAppCreated?: boolean; publish: boolean }, fromApplicationSettings.State]) => {
                const actions: any[] = [];
                let hasErrors = false;
                const processConfigData = payload.processConfigModel.map((processConfig: ProcessConfig) =>
                    ({
                        ...processConfig,
                        cronExpression: CronConfig.getCronStringForLocalTimezone(processConfig.cronConfig)
                    }));

                const observables = [
                    this.applicationSettingsService
                        .saveIntegrationProcessConfig(state.integration.integrationId, processConfigData)
                        .pipe(
                            take(1),
                            map((processConfigData: ProcessConfig[]) => {
                                actions.push(new AssignIntegrationProcessConfigModel(processConfigData));
                            }),
                            catchError(error => {
                                hasErrors = true;
                                const message = 'Could not save Sync Schedule';
                                this.snackBarService.showSnackBarMessage(message, 3000);
                                return observableOf({ type: 'FAIL', payload: {error: error}});
                            })
                        )
                ];

                if (payload.expirationTs || payload.expirationTs !== null) {
                    const expirationTsData = payload.expirationTs;
                    observables.push(
                        this.integrationService
                            .saveExpirationDate(state.integration.integrationId, expirationTsData)
                            .pipe(
                                take(1),
                                map((expirationTsData: Date) => {
                                    actions.push(new AssignApplicationExpirationDate(expirationTsData));
                                }),
                                catchError(error => {
                                    hasErrors = true;
                                    const message = 'Could not save Expiration Time';
                                    this.snackBarService.showSnackBarMessage(message, 3000);
                                    return observableOf({ type: 'FAIL', payload: {error: error}});
                                })
                            )
                    );
                }

                return combineLatest(observables).pipe(
                    mergeMap(() => {
                        if (payload.isAppCreated && !hasErrors) {
                            this.snackBarService.showSnackBarMessage(MESSAGES.SAVED, 3000);
                        }

                        if (payload.publish) {
                            actions.push({
                                type: ApplicationSettingsActions.PUBLISH
                            });
                        }

                        return actions;
                    })
                );
            })
        )
    );

    getPredicatesMetadata = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_PREDICATES_METADATA),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            concatMap(([action, state]: [ApplicationSettingsActions.GetPredicatesMetadata, fromApplicationSettings.State]) =>
                // concatMap(() => {
                this.integrationService.getPredicatesMetadata(FilterDefaultPredicateMap)
                    .pipe(
                        mergeMap(
                            (predicatesMetadata: MetadataModel[]) => {
                                predicatesMetadata = FiltersListService.filterDeniedPredicates(predicatesMetadata);

                                return [
                                    {
                                        type: ApplicationSettingsActions.SET_PREDICATES_METADATA,
                                        payload: predicatesMetadata
                                    }
                                ];
                            }
                        ),
                        catchError(error => {
                            // check to prevent appearance message on logout
                            if (state.application && state.application.guid) {
                                const message = this.GET_INTEGRATION_RULES_ERROR_MESSAGE;
                                this.snackBarService.showSnackBarMessage(message, 5000);
                            }

                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    )
            )
        )
    );

    getApplicationFeatureToggles = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_APPLICATION_FEATURE_TOGGLES),
            map((action: ApplicationSettingsActions.GetApplicationFeatureToggles) => action.payload),
            withLatestFrom(
                this.authenticatedUserService.authenticatedUser$,
                this.store.pipe(select(getApplicationSettings))
            ),
            concatMap(([{featureToggleLevel, defaultApplicationGuid}, user, state]
                           : [{ featureToggleLevel: FEATURE_TOGGLE_LEVEL; defaultApplicationGuid: string }, User, fromApplicationSettings.State]
                ) => {
                    let getApplicationFeatureToggles$: Observable<FeatureToggleModel[]> =
                        this.featureTogglesService.getApplicationFeatureToggles(
                            state.application.guid,
                            state.application.defaultApplicationGuid || defaultApplicationGuid,
                            featureToggleLevel
                        );

                    if (featureToggleLevel === FEATURE_TOGGLE_LEVEL.DISTRICT_APPLICATION) {
                        getApplicationFeatureToggles$ = forkJoin(
                            getApplicationFeatureToggles$,
                            this.featureTogglesService.getDistrictFeatureTogglesOnlyForApp(
                                state.application.ownerId || user.orgGuid,
                                state.application.defaultApplicationGuid || defaultApplicationGuid
                            ).pipe(
                                map((districtFeatureTogglesList: FeatureToggleModel[]) => {
                                    districtFeatureTogglesList = districtFeatureTogglesList.map((featureToggle: FeatureToggleModel) => {
                                        featureToggle.visible = SHOW_DISTRICT_FEATURE_TOGGLE_ON_DISTRICT_APPLICATION_LEVEL
                                            .includes(featureToggle.keyName);
                                        return featureToggle;
                                    });
                                    return districtFeatureTogglesList;
                                })
                            )
                        ).pipe(
                            map(([applicationFeatureTogglesList, districtFeatureTogglesList]:
                                     [FeatureToggleModel[], FeatureToggleModel[]]) =>
                                [...applicationFeatureTogglesList, ...districtFeatureTogglesList]
                            )
                        );
                    }

                    return getApplicationFeatureToggles$.pipe(
                            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                            mergeMap(([featureTogglesList, updatedState]: [FeatureToggleModel[], fromApplicationSettings.State]) => {

                                    const actions = [<Action>{
                                        type: ApplicationSettingsActions.SET_FEATURE_TOGGLES,
                                        payload: {application: featureTogglesList}
                                    }];

                                    // add schedule item if needed
                                    const updatedIntegration = updatedState?.integration;
                                    if (updatedIntegration?.integrationId) {

                                        const otherDS = (updatedIntegration.source.dataSourceType === DataSourceType.EduTone) ? 'target' : 'source';
                                        const isSupportIncrementalSyncFeatureToggleIsActive = featureTogglesList?.some((featureToggle: FeatureToggleModel) =>
                                            (featureToggle.keyName === ENABLE_SUPPORT_INCREMENTAL_SYNC)
                                            && featureToggle.enabled
                                        );

                                        if ((updatedIntegration?.processConfigModel?.length === 1)
                                            && isSupportIncrementalSyncFeatureToggleIsActive) {

                                            const processConfigModel = ApplicationSettingsService.getExtendedProcessConfig(
                                                updatedIntegration.processConfigModel,
                                                updatedIntegration[otherDS].dataSourceType,
                                                updatedIntegration.mode,
                                                updatedIntegration[otherDS].defaultSchemaName,
                                                isSupportIncrementalSyncFeatureToggleIsActive
                                            );

                                            // add schedule item if needed
                                            actions.push(new AssignIntegrationProcessConfigModel(processConfigModel));
                                        }
                                    }

                                    return actions;
                                }
                            ),
                            catchError(error => {
                                // check to prevent appearance message on logout
                                if (state.application && state.application.guid) {
                                    const message = this.GET_APPLICATION_DATA_ERROR_MESSAGE;
                                    this.snackBarService.showSnackBarMessage(message, 5000);
                                }

                                return observableOf({type: 'FAIL', payload: {error: error}});
                            })
                        );
                }
            )
        )
    );

    getIntegrationFeatureToggles = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_INTEGRATION_FEATURE_TOGGLES),
            map((action: ApplicationSettingsActions.GetIntegrationFeatureToggles) => action.payload),
            withLatestFrom(
                this.authenticatedUserService.authenticatedUser$,
                this.store.pipe(select(getApplicationSettings))
            ),
            concatMap((
                [{integrationId, defaultAppGuid}, user, state]
                    : [{ integrationId: string, defaultAppGuid: string }, User, fromApplicationSettings.State]) =>
                this.featureTogglesService.getIntegrationFeatureToggles(integrationId, defaultAppGuid)
                    .pipe(
                        mergeMap((featureTogglesList: FeatureToggleModel[]) => {

                                return [{
                                    type: ApplicationSettingsActions.SET_FEATURE_TOGGLES,
                                    payload: {integration: featureTogglesList}
                                }];
                            }
                        ),
                        catchError(error => {
                            // check to prevent appearance message on logout
                            if (state.application && state.application.guid) {
                                // ToDo: set appropriate message fot request view
                                const message = this.GET_APPLICATION_DATA_ERROR_MESSAGE;
                                this.snackBarService.showSnackBarMessage(message, 5000);
                            }

                            return observableOf({type: 'FAIL', payload: {error: error}});
                        })
                    )
            )
        )
    );

    publish = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.PUBLISH),
            map((action: ApplicationSettingsActions.Publish) => action),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap((d) => observableForkJoin(
                observableOf(d[0]),
                observableOf(d[1]),
                observableOf(this.authenticatedUserService.authenticatedUser)
            )),
            concatMap(([action, state, user]: [ApplicationSettingsActions.Publish, fromApplicationSettings.State, User]) => {

                const integrationId = state.integration?.integrationId;

                return this.http.post<Application>(`${environment.datahubBaseURI}/api/v2/applications/${state.application.guid}/publish`, '')
                    .pipe(
                        withLatestFrom(this.store.pipe(select(getApplicationSettings))),
                        mergeMap(
                            ([application, updatedState]: [Application, fromApplicationSettings.State]) => {

                                const actions: any[] = [<Action>{
                                    type: ApplicationSettingsActions.REGENERATE_APPLICATION_PII_SHIELD_LEVEL,
                                    payload: {integrationId: integrationId, applicationGuid: updatedState.application.guid}
                                }];

                                if (updatedState.application && (updatedState.application.guid === application.guid)) {
                                    actions.push({
                                        type: ApplicationSettingsActions.ASSIGN_APPLICATION,
                                        payload: application
                                    });
                                }

                                if (this.authenticatedUserService.isVendor(user)) {
                                    actions.push(new IntegrationGettingDefaultAction({applicationId: application.guid}));
                                    actions.push(new LoadVendorApps({user: user}));
                                }

                                const isPublishChanges = <ApplicationType>state.application.type === ApplicationType.PUBLIC;
                                const message = isPublishChanges ? this.PUBLISH_CHANGES_SUCCESSFUL_MESSAGE : this.PUBLISH_SUCCESSFUL_MESSAGE;
                                this.snackBarService.showSnackBarMessage(message, 3000);

                                if (action.payload
                                    && !isPublishChanges
                                    && (<FEATURE_TYPES>application.featureType === FEATURE_TYPES.SSO)
                                    && !application.passportSsoService) {

                                    const config = new MatDialogConfig();
                                    config.viewContainerRef = application.featureType === FEATURE_TYPES.SSO ? null : action.payload;
                                    config.autoFocus = false;

                                    const dialogRef = this.matDialog.open(ConfirmDialogComponent, config);
                                    dialogRef.componentInstance.item = 'error';
                                    dialogRef.componentInstance.header = 'Almost done';
                                    dialogRef.componentInstance.target = 'Thank you for publishing the application. Our development team will adjust the necessary configurations. We will additionally inform you once the process is completed.';

                                    dialogRef.afterClosed()
                                        .subscribe(() => {
                                        });
                                }

                                return actions;
                            }
                        ),
                        catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                    )
            })
        )
    );

    unPublish = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.UNPUBLISH),
            map((action: ApplicationSettingsActions.UnPublish) => action),
            withLatestFrom(this.store.pipe(select(getApplicationSettings)), this.authenticatedUserService.authenticatedUser$),
            concatMap(([action, state, user]) => this.http.post<Application>(`${environment.datahubBaseURI}/api/v2/applications/${state.application.guid}/unpublish`,
                '')
                .pipe(
                    mergeMap(
                        (application: Application) => {
                            const actions: any[] = [{
                                type: ApplicationSettingsActions.ASSIGN_APPLICATION,
                                payload: application
                            }];
                            if (this.authenticatedUserService.isVendor(user)) {
                                actions.push(new IntegrationGettingDefaultAction({applicationId: application.guid}));
                                actions.push(new LoadVendorApps({user: user}));
                            }
                            this.searchService.removeFromRecentByGuid(application.guid);

                            const message = this.UNPUBLISH_SUCCESSFUL_MESSAGE;
                            this.snackBarService.showSnackBarMessage(message, 3000);

                            return actions;
                        }
                    ),
                    catchError(error => observableOf({type: 'FAIL', payload: {error: error}}))
                ))
        )
    );

    getOriginalIntegration = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.GET_ORIGINAL_INTEGRATION),
            map((action: ApplicationSettingsActions.GetOriginalIntegration) => action),
            concatMap((action) => {
                let result: Observable<Integration | {}>;
                if (action.payload) {
                    result =
                        this.http.get<Integration>(`${environment.datahubBaseURI}/api/v2/applications/${action.payload}/integrations/original`)
                            .pipe(map((integration: Integration) => {
                                if (integration?.dictionaries) {
                                    integration.dictionaries = integration.dictionaries.filter((dictionary: DictionaryModel) =>
                                        !dictionary?.dictionaryName?.startsWith('deleted_')
                                    );
                                }
                                return integration;
                            })
                        );
                } else {
                    result = observableOf({});
                }
                return result
                    .pipe(
                        mergeMap(
                            (integration: Integration) => {
                                return [{
                                    type: ApplicationSettingsActions.SET_IS_ORIGINAL_INTEGRATION,
                                    payload: !!integration.integrationId
                                }];
                            }),
                        catchError(error => {
                            error.message = this.GET_APPLICATION_DATA_ERROR_MESSAGE;
                            return from([
                                {
                                    type: 'FAIL',
                                    payload: {error: error, doNotStopSpinner: true}
                                },
                                {
                                    type: ApplicationSettingsActions.SET_ERROR,
                                    payload: error
                                }
                            ]);
                        }),
                        takeUntil(this.store.pipe(select(isAuthenticated), filter(authenticated => !authenticated)))
                    );
            })
        )
    );

    saveSpinnerInstanceToAddAppEffect = createEffect(() =>
            this.actions$.pipe(
                ofType(ApplicationSettingsActions.SAVE_SPINNER_INSTANCE_TO_ADD_APP_EFFECT),
                map((action: ApplicationSettingsActions.SaveSpinnerInstanceToAddAppEffect) => action.payload),
                concatMap((spinner: Spinner) => {
                    this.spinner = spinner;
                    return observableOf({});
                })
            ),
        {dispatch: false}
    );

    fail = createEffect(() =>
        this.actions$.pipe(
            ofType(ApplicationSettingsActions.FAIL),
            map((action: ApplicationSettingsActions.Fail) => action.payload),
            withLatestFrom(this.store.pipe(select(getApplicationSettings))),
            mergeMap(([payload, state]) => {
                this.logService.logWarn(payload.error.message ? payload.error.message : payload.error.toString());

                const isSpinnerActive = this.spinner && this.spinner.active;

                if (isSpinnerActive && !payload.doNotStopSpinner) {
                    this.spinner.stop();
                }

                return [
                    {
                        type: ApplicationSettingsActions.SET_IS_LOADING,
                        payload: state.isLoading && !!payload.doNotStopSpinner
                    }
                ];
            })
        )
    );

    constructor(private actions$: Actions,
                private http: HttpClient,
                private store: Store<fromApplicationSettings.State>,
                private applicationSettingsService: ApplicationSettingsService,
                private userPreferencesService: UserPreferencesService,
                private authenticatedUserService: AuthenticatedUserService,
                private authenticationService: AuthenticationService,
                private announcementService: AnnouncementService,
                private tokenService: TokenService,
                private searchService: NgcSearchService,
                private privacyPolicyService: PrivacyPolicyService,
                private logService: LogService,
                private snackBarService: SnackBarService,
                private integrationService: IntegrationService,
                private matDialog: MatDialog,
                private router: Router,
                private orgsService: OrgsService,
                private customerService: CustomerService,
                private featureTogglesService: FeatureTogglesService,
                private piiShieldService: PIIShieldService,
    ) {
        this.snackBar = this.snackBarService.snackBar;
    }
}
